import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PptSlideViewer from './PptSlideViewer';
import VoiceNoteInput from './VoiceNoteInput';
import LiveMeetingCopilotMascot from './components/MascotFloating/LiveMeetingCopilotMascot';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Empty,
  Input,
  Modal,
  Progress,
  QRCode,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Timeline,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadFile } from 'antd';
import {
  ApartmentOutlined,
  AudioOutlined,
  BookOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  FileDoneOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  InboxOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ProjectOutlined,
  ReadOutlined,
  ReloadOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  SettingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { io, Socket } from 'socket.io-client';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import './index.css';

const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;

type OutputScope = 'pre_only' | 'pre_plus_rd';
type ArtifactStatus = 'locked' | 'ready' | 'queued' | 'running' | 'review' | 'confirmed' | 'failed' | 'ppt_review';
type TaskStatus = 'waiting' | 'running' | 'done';
type RecordStatus = 'idle' | 'connecting' | 'recording' | 'stopping' | 'error';

interface MaterialItem { id: string; name: string; kind: string; meta: string; status: '已解析' | '转写中' | '待确认'; content?: string; selected?: boolean; }
interface MeetingArtifactRequirement { id: string; title: string; artifactType: string; outputFormat?: 'word' | 'ppt' | 'html' | 'md' | 'image'; priority?: 'high' | 'medium' | 'low'; sourceType?: 'explicit' | 'inferred'; reason?: string; description: string; requiredContents?: string[]; evidence?: string[]; userSupplement?: string; status?: 'pending' | 'generating' | 'generated' | 'failed' | 'ignored'; generatedArtifactId?: string; }
interface ArtifactItem { id: string; level: string; name: string; owner: string; type: 'pre' | 'rd' | 'base'; status: ArtifactStatus; desc: string; preview: string; format?: 'word' | 'ppt' | 'html' | 'md' | 'image'; progress?: number; content?: string; error?: string; generatedAt?: string; fileSize?: number; step?: string; steps?: string[]; references?: { materials?: string[]; knowledgeBase?: boolean }; pptProjectId?: string; meetingRequirement?: MeetingArtifactRequirement; }
interface AgentTask { id: string; agent: string; role: string; job: string; status: TaskStatus; progress: number; }
interface LlmModelOption { id: string; name: string; provider?: { name?: string; code?: string }; }
interface DemandTask { id: string; name: string; status: string; createdAt: string; updatedAt: string; modelId?: string; artifacts?: any; intelligence?: IntelligenceState | null; }
interface IntelligenceSource { id: string; title: string; url: string; domain?: string; summary: string; score?: number; reason?: string; fetchedAt: string; }
interface IntelligenceState { enabled: boolean; status: 'idle' | 'keywording' | 'searching' | 'analyzing' | 'analysis_failed' | 'done' | 'empty' | 'failed'; query?: string; keywords?: string[]; injected?: boolean; error?: string; sources: IntelligenceSource[]; }
interface TencentMeetingItem { id?: string; meeting_id?: string; meetingId?: string; subject?: string; title?: string; meeting_name?: string; start_time?: string; end_time?: string; startTime?: string; endTime?: string; [key: string]: any; }
interface LiveMascotInsight { type: 'speaking' | 'alert' | 'knowledge' | 'thinking' | 'error'; message: string; createdAt: number; }

// 小灵会议插嘴：一条已落库的插嘴记录（确认/取消都留档到会议任务）
// 分类对齐 docs/plans/ai-demand-center-live-meeting-copilot-plan.md 的8类介入场景 + uncertainty 答疑
export interface MascotInterjectionRecord {
  id: string;
  taskId?: string | null;
  category:
    | 'business_association' | 'point_connection' | 'requirement_clarification' | 'concept_correction'
    | 'next_thinking_direction' | 'missing_dimension' | 'industry_knowledge' | 'experience_mapping'
    | 'uncertainty' | 'issue' | 'supplement' | 'summary' | string;
  title: string;
  speech: string;
  suggestion?: string | null;
  evidence?: string | null;
  status: 'pending' | 'spoken' | 'dismissed' | string;
  createdAt?: string;
}

const TEMPLATES = [
  { id: 'tpl-highway-rd', name: '高速公路常规研发软件项目', desc: '适合收费、养护、隧道、服务区、路网等软件项目。', artifactIds: ['blueprint', 'requirement', 'prd', 'plan', 'timeline', 'handoff'], chips: ['总体思路', '需求分析', 'PRD', '项目计划', '执行计划', '研发交接包'] },
  { id: 'tpl-problem', name: '生产问题解决专项', desc: '围绕真实生产问题开会，形成整改方案和复盘材料。', artifactIds: ['problemRoot', 'solution', 'risk'], chips: ['根因分析', '整改方案', '风险清单'] },
  { id: 'tpl-sales', name: '信息化建设规划与领导汇报', desc: '适合领导关注的方向性、思路性信息化建设规划，沉淀建设路径、推进抓手和汇报材料。', artifactIds: ['blueprint', 'valuePlan', 'pptOutline', 'speech', 'newsRelease'], chips: ['总体思路', '建设规划', 'PPT大纲', '领导汇报稿', '公众号新闻稿'] },
];

const ANALYSIS_MODES = [
  { key: 'standard', name: '通用标准版', desc: '职场通用格式：基本信息、议题、讨论、决议、待办、遗留问题', icon: '📋' },
  { key: 'minimal', name: '极简速记版', desc: '只保留核心结论、分工安排、限期任务，高度凝练，快速查阅', icon: '⚡' },
  { key: 'formal', name: '正式公文版', desc: '对外汇报/管理会议/项目评审用，含工作督办清单和签发人', icon: '🏛️' },
  { key: 'structured', name: '结构化分析版', desc: '技术/业务复盘侧重落地：背景目标、方案争议、风险提示', icon: '🔍' },
];

const INITIAL_MATERIALS: MaterialItem[] = [
  { id: 'm1', name: '会议实时转写与HTML纪要', kind: '录音/纪要', meta: '录音分析后自动沉淀为首要资料', status: '待确认', selected: true },
];

const INITIAL_ARTIFACTS: ArtifactItem[] = [
  { id: 'minutes', level: '基础', name: '会议纪要', owner: '会议纪要 Agent', type: 'base', status: 'confirmed', desc: '所有后续产物的唯一事实输入。', preview: '录音完成后生成 HTML 会议纪要。', format: 'html', progress: 100 },
  { id: 'blueprint', level: '产前', name: '总体思路', owner: '产品策略 Agent', type: 'pre', status: 'ready', desc: '说明项目要做什么、给谁用、解决什么问题、先做哪些内容。', preview: '等待根据会议纪要生成。', format: 'word' },
  { id: 'requirement', level: '产前', name: '需求分析报告', owner: '需求分析 Agent', type: 'pre', status: 'ready', desc: '梳理角色、场景、业务流程、功能需求。', preview: '等待根据会议纪要生成。', format: 'word' },
  { id: 'prd', level: '产前', name: 'PRD 产品设计稿', owner: '产品设计 Agent', type: 'pre', status: 'ready', desc: '页面结构、交互流程、字段说明。', preview: '等待根据会议纪要生成。', format: 'word' },
  { id: 'plan', level: '产前', name: '项目实施计划', owner: '项目经理 Agent', type: 'pre', status: 'ready', desc: '阶段拆分、风险清单和确认节点。', preview: '等待根据会议纪要生成。', format: 'word' },
  { id: 'timeline', level: '产前', name: '执行计划与时间节点', owner: '项目经理 Agent', type: 'pre', status: 'ready', desc: '关键里程碑、交付节点、依赖关系和排期表。', preview: '等待根据会议纪要生成。', format: 'word' },
  { id: 'problemRoot', level: '专项', name: '根因分析报告', owner: '复盘分析 Agent', type: 'pre', status: 'locked', desc: '生产问题/业务问题根因、证据、影响面分析。', preview: '模板选择后进入生成队列。', format: 'word' },
  { id: 'solution', level: '专项', name: '整改方案', owner: '方案设计 Agent', type: 'pre', status: 'locked', desc: '整改目标、动作、责任、里程碑、验收标准。', preview: '模板选择后进入生成队列。', format: 'word' },
  { id: 'risk', level: '专项', name: '风险清单', owner: '风险评估 Agent', type: 'pre', status: 'locked', desc: '列出风险、概率、影响、应对和兜底方案。', preview: '模板选择后进入生成队列。', format: 'html' },
  { id: 'valuePlan', level: '规划', name: '信息化建设规划思路', owner: '规划方案 Agent', type: 'pre', status: 'locked', desc: '面向领导决策的建设背景、总体思路、目标架构、重点任务、推进路径和预期成效。', preview: '模板选择后进入生成队列。', format: 'word' },
  { id: 'pptOutline', level: '规划', name: '领导汇报PPT大纲', owner: '汇报策划 Agent', type: 'pre', status: 'locked', desc: '围绕信息化建设规划形成页级汇报逻辑、核心观点和讲解备注。', preview: '模板选择后进入生成队列。', format: 'ppt' },
  { id: 'speech', level: '规划', name: '领导汇报稿', owner: '汇报写作 Agent', type: 'pre', status: 'locked', desc: '突出建设方向、工作思路、推进抓手和示范价值的连续汇报稿。', preview: '模板选择后进入生成队列。', format: 'word' },
  { id: 'newsRelease', level: '宣传', name: '公众号新闻稿', owner: '宣传稿 Agent', type: 'pre', status: 'locked', desc: '用于公众号发布的宣传新闻稿，突出建设亮点、应用成效、典型价值和可推广性。', preview: '模板选择后进入生成队列。', format: 'word' },
  { id: 'tech', level: '研发', name: '技术方案与接口清单', owner: '架构师 Agent', type: 'rd', status: 'locked', desc: '技术类产物默认锁定。', preview: '研发产物将补充系统架构、数据模型、接口清单。', format: 'word' },
  { id: 'handoff', level: '交接', name: 'AI全流程研发交接包', owner: '交接编排 Agent', type: 'rd', status: 'locked', desc: '把已确认产物转换为研发团队可执行的任务上下文。', preview: '交接包包含范围、禁止事项、接口依赖、验收标准。', format: 'md' },
];

const MEETING_ARTIFACT_REQUIREMENTS_ID = '__meeting_artifact_requirements__';

function meetingRequirementArtifactId(id: string) { return `meeting-${id}`; }

function buildMeetingArtifactItem(req: MeetingArtifactRequirement): ArtifactItem {
  const required = req.requiredContents?.filter(Boolean).slice(0, 4).join('、') || '按会议内容自动组织章节';
  return {
    id: meetingRequirementArtifactId(req.id),
    level: req.sourceType === 'explicit' ? '会议明确' : 'AI建议',
    name: req.title || '会议需求产物',
    owner: '会议产物 Agent',
    type: 'pre',
    status: req.generatedArtifactId ? 'review' : 'ready',
    desc: req.description || req.reason || '根据本次会议自动识别的待生成产物。',
    preview: `需包含：${required}`,
    format: req.outputFormat || 'word',
    meetingRequirement: req,
  };
}

function artifactFormatLabel(format?: ArtifactItem['format'] | MeetingArtifactRequirement['outputFormat']) {
  if (format === 'word') return 'Word';
  if (format === 'ppt') return 'PPT';
  if (format === 'md') return 'Markdown';
  if (format === 'image') return '图片 / 工程图';
  return format?.toUpperCase() || 'HTML';
}

function imageArtifactUrl(item: ArtifactItem) {
  if (item.format !== 'image' || !item.content) return '';
  try {
    const raw = JSON.parse(item.content).imageUrl || '';
    if (!raw) return '';
    // /api/design-creator/assets/:id/view 需带 token 鉴权，否则 <img> 请求 401 无法显示
    if (/^(https?:|data:|blob:)/i.test(raw) || raw.includes('token=')) return raw;
    const token = useAuthStore.getState().token || '';
    return `${raw}${raw.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
  } catch { return ''; }
}

const INITIAL_TASKS: AgentTask[] = [];

function statusLabel(status: ArtifactStatus) {
  const map: Record<ArtifactStatus, string> = { locked: '未纳入', ready: '可生成', queued: '排队中', running: '生成中', review: '待确认', confirmed: '已确认', failed: '失败', ppt_review: '待生图' };
  return map[status];
}
function statusClass(status: ArtifactStatus) { return `adc-demo-status adc-demo-status-${status}`; }

function formatFileSize(size?: number) {
  if (!size || size <= 0) return '待生成';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

/** 为PPT产物获取有意义的“文件大小”展示文本（幻灯片数），避免把JSON content算成几十字节 */
function pptSizeLabel(item: ArtifactItem): string {
  if (item.fileSize && item.fileSize > 0) return formatFileSize(item.fileSize);
  let slideCount = 0;
  try { slideCount = JSON.parse(item.content || '{}').slideCount || 0; } catch {}
  return `PPT · ${slideCount || '?'} 页`;
}

function formatDateTime(value?: string) {
  if (!value) return '待生成';
  return new Date(value).toLocaleString();
}

const GENERATION_STEPS = [
  '已进入生成队列',
  '正在读取会议纪要与共创资料',
  '正在匹配知识库',
  '正在等待 AI 调用结果',
  '正在整理产物正文与元数据',
  '已生成，等待确认',
];

function LightFlowCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    let frame = 0;
    let raf = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const draw = () => {
      frame += 1;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const gradient = ctx.createLinearGradient(0, 0, w, h);
      gradient.addColorStop(0, '#f8fbff');
      gradient.addColorStop(0.55, '#f6fbf9');
      gradient.addColorStop(1, '#fff8f0');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 6; i += 1) {
        const x = (w * (0.1 + i * 0.16) + Math.sin(frame * 0.006 + i) * 18) % w;
        const y = h * (0.16 + (i % 3) * 0.24) + Math.cos(frame * 0.005 + i) * 14;
        const rg = ctx.createRadialGradient(x, y, 0, x, y, 150);
        rg.addColorStop(0, i % 2 ? 'rgba(14,165,233,.08)' : 'rgba(20,184,166,.08)');
        rg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = rg;
        ctx.beginPath();
        ctx.arc(x, y, 150, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    resize();
    draw();
    window.addEventListener('resize', resize);
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf); };
  }, []);
  return <canvas className="adc-demo-canvas" ref={canvasRef} aria-hidden="true" />;
}

async function float32ToPcm16k(input: Float32Array, inputRate: number) {
  const ratio = inputRate / 16000;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const pcm = new Int16Array(length);
  for (let i = 0; i < length; i += 1) {
    const sample = input[Math.floor(i * ratio)] || 0;
    const clipped = Math.max(-1, Math.min(1, sample));
    pcm[i] = clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff;
  }
  return pcm;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatMinutesHtml(raw: string, taskName: string, modeName: string) {
  const text = (raw || '').trim();
  if (!text) return '';
  const hasHtml = /<\s*(h1|h2|h3|section|article|table|ul|ol|p|div|blockquote)\b/i.test(text);
  if (hasHtml) return text;
  const paragraphs = text.split(/\n{2,}/).map(item => item.trim()).filter(Boolean);
  const inlineLines = paragraphs.length ? paragraphs : text.split(/(?<=[。！？；])\s*/).map(item => item.trim()).filter(Boolean);
  const highlights = inlineLines.slice(0, 4);
  const rest = inlineLines.slice(4);
  return `
    <article class="adc-minutes-document">
      <header class="adc-minutes-cover">
        <div class="adc-minutes-eyebrow">AI 会议纪要</div>
        <h1>${escapeHtml(taskName || 'AI需求共创会议纪要')}</h1>
        <p>${escapeHtml(modeName)} · 自动整理 · 可作为后续产物生成依据</p>
      </header>
      <section class="adc-minutes-section">
        <h2>会议要点</h2>
        <div class="adc-minutes-highlight-grid">
          ${highlights.map((item, index) => `<div class="adc-minutes-highlight"><span>${String(index + 1).padStart(2, '0')}</span><p>${escapeHtml(item)}</p></div>`).join('')}
        </div>
      </section>
      ${rest.length ? `<section class="adc-minutes-section"><h2>详细记录</h2>${rest.map(item => `<p>${escapeHtml(item)}</p>`).join('')}</section>` : ''}
    </article>`;
}

function buildMinutesExportName(taskName: string) {
  const safeTaskName = (taskName || 'AI需求共创')
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_')
    .replace(/\s+/g, ' ')
    .replace(/[.\s]+$/g, '')
    .trim()
    .slice(0, 80);
  return `${safeTaskName || 'AI需求共创'}_原始会议纪要`;
}

/**
 * 会议纪要正文渲染（问题1根因修复）：
 * 后端要求 AI 输出“DOCTYPE 到 </html> 的完整 HTML 文档 + 内嵌 <style>”。之前用
 * dangerouslySetInnerHTML 直接注入页面，文档里的 body/html/通配符等全局 CSS 会泄漏到
 * 整个应用（例如 body{max-width:...;margin:0 auto}），导致整页被左右压缩成“移动端布局”；
 * 且 antd Tabs 默认保留已挂载的隐藏面板，display:none 里的 <style> 依然全局生效，
 * 所以切任何 tab 都无法恢复。是否触发取决于 AI 当次生成的 CSS —— 因此“有概率出现”。
 * 修复：凡是带自有样式/文档结构的纪要，一律放入沙箱 iframe 隔离渲染（与产物预览一致），
 * 并按内容自适应高度；纯文本纪要仍用本页样式包装展示。
 */
function MinutesHtmlView({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const isStandaloneDoc = /<!DOCTYPE|<\s*(html|head|body|style|link|meta)\b/i.test(html);
  const fitHeight = () => {
    const frame = iframeRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc) return;
    const height = Math.max(doc.documentElement?.scrollHeight || 0, doc.body?.scrollHeight || 0);
    if (height > 0) frame.style.height = `${height + 24}px`;
  };
  if (!isStandaloneDoc) {
    return <div className="adc-minutes-html adc-minutes-html-polished" dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return (
    <iframe
      ref={iframeRef}
      className="adc-minutes-html-iframe"
      title="会议纪要"
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={fitHeight}
    />
  );
}

/** 调用后端 LibreOffice 转换 HTML → DOCX/PDF 并触发下载 */
async function downloadOfficeFile(
  html: string, name: string, format: 'docx' | 'pdf', token: string,
  onOk: (msg: string) => void, onErr: (msg: string) => void,
  onStart?: () => unknown, onDone?: (key: unknown) => void,
) {
  const key = onStart ? onStart() : '';
  try {
    const res = await fetch('/api/ai-demand-center/convert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ html: html || '', format, filename: name }),
    });
    if (!res.ok) { const data = await res.json().catch(() => ({})); throw new Error(data.message || `HTTP ${res.status}`); }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    onOk(`已下载 ${name}.${format}`);
  } catch (e: any) {
    onErr(e.message || '下载失败');
  } finally {
    if (onDone) onDone(key);
  }
}

export default function AiDemandCenterPage() {
  const [messageApi, contextHolder] = message.useMessage();
  const authUser = useAuthStore(state => state.user);
  const [scope, setScope] = useState<OutputScope>('pre_only');
  const [templateId, setTemplateId] = useState('tpl-highway-rd');
  const [artifacts, setArtifacts] = useState<ArtifactItem[]>(INITIAL_ARTIFACTS);
  const [meetingArtifactRequirements, setMeetingArtifactRequirements] = useState<MeetingArtifactRequirement[]>([]);
  const [tasks, setTasks] = useState<AgentTask[]>(INITIAL_TASKS);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [preview, setPreview] = useState<ArtifactItem | null>(null);
  const [mascotAnnouncement, setMascotAnnouncement] = useState<{ id: string; text: string } | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [materials, setMaterials] = useState<MaterialItem[]>(INITIAL_MATERIALS);
  const [useKnowledgeBase, setUseKnowledgeBase] = useState(true);
  const [artifactKeyword, setArtifactKeyword] = useState('');
  const [selectedArtifactIds, setSelectedArtifactIds] = useState<string[]>(TEMPLATES[0].artifactIds);
  const [recordStatus, setRecordStatus] = useState<RecordStatus>('idle');
  const [transcript, setTranscript] = useState('');
  const [partialText, setPartialText] = useState('');
  const [models, setModels] = useState<LlmModelOption[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string>();
  const [tencentMeetingStatus, setTencentMeetingStatus] = useState<{ authorized?: boolean; message?: string } | null>(null);
  const [tencentStatusLoading, setTencentStatusLoading] = useState(false);
  const [tmeetAuthModalOpen, setTmeetAuthModalOpen] = useState(false);
  const [tmeetAuthUrl, setTmeetAuthUrl] = useState('');
  const [tmeetAuthPhase, setTmeetAuthPhase] = useState<'starting' | 'waiting' | 'success' | 'failed'>('starting');
  const [tmeetAuthError, setTmeetAuthError] = useState('');
  const tmeetAuthPollRef = useRef<number | null>(null);
  const [tencentMeetings, setTencentMeetings] = useState<TencentMeetingItem[]>([]);
  const [tencentCurrentMeetings, setTencentCurrentMeetings] = useState<TencentMeetingItem[]>([]);
  const [recentTencentMeetings, setRecentTencentMeetings] = useState<TencentMeetingItem[]>([]);
  const [tencentMeetingLoading, setTencentMeetingLoading] = useState(false);
  const [tencentExtractLoading, setTencentExtractLoading] = useState(false);
  const [tencentCreateLoading, setTencentCreateLoading] = useState(false);
  const [tencentSelectedMeetingId, setTencentSelectedMeetingId] = useState('');
  const [tencentRecordId, setTencentRecordId] = useState('');
  const [tencentExtractMode, setTencentExtractMode] = useState<'recording' | 'minutes'>('recording');
  const [tencentSubject, setTencentSubject] = useState('AI需求共创会议');
  const nextHalfHour = () => { const d = new Date(); const m = Math.ceil(d.getMinutes() / 30) * 30; d.setMinutes(m, 0, 0); if (d.getTime() - Date.now() < 60000) d.setMinutes(d.getMinutes() + 30); return d; };
  const [tencentStart, setTencentStart] = useState(() => { const d = nextHalfHour(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; });
  const [tencentDuration, setTencentDuration] = useState(60);
  const [tencentMeetingInfo, setTencentMeetingInfo] = useState<any>(null);
  const [tencentMeetingInfoLoading, setTencentMeetingInfoLoading] = useState(false);
  const [pickerExpanded, setPickerExpanded] = useState(true);

  // localStorage 持久化近期 meeting_code，解决刷新后"无状态"会议丢失问题
  const TENCENT_CODE_KEY = 'tencent-meeting-codes';
  const loadPersistedMeetingCodes = (): string[] => {
    try { const raw = localStorage.getItem(TENCENT_CODE_KEY); return raw ? JSON.parse(raw) : []; } catch { return []; }
  };
  const persistMeetingCodes = (codes: string[]) => {
    try { localStorage.setItem(TENCENT_CODE_KEY, JSON.stringify(codes.slice(0, 20))); } catch {}
  };
  const persistMeetingCodeFromItem = (meeting: TencentMeetingItem) => {
    const code = meeting.meeting_code || (meeting as any).meetingCode || '';
    if (!code) return;
    const current = loadPersistedMeetingCodes();
    const codeStr = String(code).replace(/[^0-9]/g, '');
    if (!codeStr || current.includes(codeStr)) return;
    current.unshift(codeStr);
    persistMeetingCodes(current);
  };

  const [artifactNotes, setArtifactNotes] = useState<Record<string, string>>({});
  const [timelineDeadline, setTimelineDeadline] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  });  const [analysis, setAnalysis] = useState('');
  const [analysisError, setAnalysisError] = useState('');
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [minutesExporting, setMinutesExporting] = useState<'docx' | 'pdf' | null>(null);
  const [analysisMode, setAnalysisMode] = useState('standard');
  const [taskName, setTaskName] = useState('');
  const [activeTab, setActiveTab] = useState('meeting');
  const [highlightRecordingBtn, setHighlightRecordingBtn] = useState(false);
  const [intelligence, setIntelligence] = useState<IntelligenceState>({ enabled: true, status: 'idle', sources: [] });
  const [demandTasks, setDemandTasks] = useState<DemandTask[]>([]);
  const [currentTaskId, setCurrentTaskId] = useState<string>('');
  const [taskLoading, setTaskLoading] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const transcriptRef = useRef('');
  const partialTextRef = useRef('');
  const currentTaskIdRef = useRef('');
  const saveTimerRef = useRef<number | null>(null);
  const lastSavedTranscriptRef = useRef('');
  const lastInsightAtRef = useRef(0);
  const [liveInsight, setLiveInsight] = useState<LiveMascotInsight | null>(null);
  const [liveInsightLoading, setLiveInsightLoading] = useState(false);
  const [mascotInterjection, setMascotInterjection] = useState<MascotInterjectionRecord | null>(null);
  const recentInterjectTitlesRef = useRef<string[]>([]);
  const mascotInterjectionRef = useRef<MascotInterjectionRecord | null>(null);
  const [meetingFileImporting, setMeetingFileImporting] = useState(false);

  const activeTemplate = useMemo(() => TEMPLATES.find(item => item.id === templateId) || TEMPLATES[0], [templateId]);
  const visibleArtifacts = useMemo(() => artifacts.filter(item => item.id !== MEETING_ARTIFACT_REQUIREMENTS_ID && (scope === 'pre_plus_rd' || item.type !== 'rd')), [artifacts, scope]);
  const generatedArtifacts = useMemo(() => visibleArtifacts.filter(item => item.id !== 'minutes' && (item.content || ['queued', 'running', 'review', 'confirmed', 'failed', 'ppt_review'].includes(item.status))), [visibleArtifacts]);
  const selectedMaterials = useMemo(() => materials.filter(item => item.selected), [materials]);
  const selectableArtifacts = useMemo(() => artifacts.filter(item => item.id !== 'minutes' && !item.meetingRequirement && (scope === 'pre_plus_rd' || item.type !== 'rd') && (!artifactKeyword.trim() || `${item.name}${item.desc}${item.level}`.includes(artifactKeyword.trim()))), [artifacts, scope, artifactKeyword]);
  const runnableArtifacts = useMemo(() => visibleArtifacts.filter(item => selectedArtifactIds.includes(item.id) && item.id !== 'minutes'), [visibleArtifacts, selectedArtifactIds]);
  const runningCount = artifacts.filter(item => item.status === 'running').length;
  const confirmedCount = generatedArtifacts.filter(item => item.status === 'confirmed').length;
  const modelOptions = models.map(item => ({ value: item.id, label: item.provider?.name ? `${item.provider.name} / ${item.name}` : item.name }));
  const fullTranscript = transcript.trim();
  const isNewTask = !currentTaskId;

  useEffect(() => {
    api.get('/llm/models').then(res => setModels(res.data || [])).catch(() => messageApi.warning('模型列表加载失败，请先在设置中配置模型'));
    fetchTasks();
    fetchTencentMeetingStatus();
  }, [messageApi]);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    partialTextRef.current = partialText;
  }, [partialText]);

  useEffect(() => {
    currentTaskIdRef.current = currentTaskId;
  }, [currentTaskId]);

  useEffect(() => {
    const ids = new Set(activeTemplate.artifactIds.concat(scope === 'pre_plus_rd' ? ['tech', 'handoff'] : []));
    setSelectedArtifactIds(Array.from(ids));
    setArtifacts(prev => prev.map(item => {
      if (item.id === 'minutes' || item.status === 'review' || item.status === 'confirmed' || item.status === 'running' || item.status === 'ppt_review') return item;
      return ids.has(item.id) ? { ...item, status: 'ready', progress: item.progress || 0 } : { ...item, status: 'locked', progress: 0 };
    }));
  }, [activeTemplate, scope]);

  useEffect(() => {
    setMaterials(prev => prev.map(item => item.id === 'm1' ? {
      ...item,
      status: analysis ? '已解析' : fullTranscript ? '待确认' : '待确认',
      content: `${fullTranscript}\n\n${analysis ? `会议纪要HTML：\n${analysis}` : ''}`.trim(),
      meta: analysis ? `转写 ${fullTranscript.length} 字 · 纪要已生成` : fullTranscript ? `转写 ${fullTranscript.length} 字 · 等待AI纪要` : '录音分析后自动沉淀为首要资料',
    } : item));
  }, [analysis, fullTranscript]);

  useEffect(() => () => {
    cleanupAudio();
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    socketRef.current?.emit('ai-demand:asr-stop');
    socketRef.current?.disconnect();
    socketRef.current = null;
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await api.get('/ai-demand-center/tasks');
      setDemandTasks(res.data || []);
    } catch { /* 页面刚加载，后端表还没建时会 404，静默处理 */ }
  };

  const saveTranscriptDraft = async (text: string, immediate = false) => {
    const content = text.trim();
    const taskId = currentTaskIdRef.current;
    if (!taskId || !content || content === lastSavedTranscriptRef.current) return;
    const run = async () => {
      try {
        await api.put(`/ai-demand-center/tasks/${taskId}`, { transcript: content, modelId: selectedModelId, status: 'recorded' });
        lastSavedTranscriptRef.current = content;
      } catch (error) {
        // 录音中保存失败不打断用户讲话，结束录音时会再保存一次并提示
        console.warn('[AI-DEMAND][ASR] autosave transcript failed', error);
      }
    };
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    if (immediate) {
      await run();
    } else {
      saveTimerRef.current = window.setTimeout(run, 1200);
    }
  };

  const ensureCurrentTask = async () => {
    if (currentTaskIdRef.current) return currentTaskIdRef.current;
    const taskRes = await api.post('/ai-demand-center/tasks', { transcript: transcriptRef.current || '', modelId: selectedModelId });
    const newTask: DemandTask = taskRes.data;
    setCurrentTaskId(newTask.id);
    currentTaskIdRef.current = newTask.id;
    setDemandTasks(prev => prev.some(item => item.id === newTask.id) ? prev : [newTask, ...prev]);
    lastSavedTranscriptRef.current = transcriptRef.current || '';
    return newTask.id;
  };

  const appendFinalTranscript = (text: string) => {
    const clean = text.trim();
    if (!clean) return transcriptRef.current;
    const prev = transcriptRef.current;
    if (prev && (prev.endsWith(clean) || prev.includes(`\n${clean}\n`) || prev.includes(`\n${clean}`))) {
      setPartialText('');
      partialTextRef.current = '';
      return prev;
    }
    const next = prev ? `${prev}\n${clean}` : clean;
    transcriptRef.current = next;
    setTranscript(next);
    saveTranscriptDraft(next);
    setPartialText('');
    partialTextRef.current = '';
    void requestLiveInsight(next, '', false);
    return next;
  };

  const requestLiveInsight = async (transcriptText = transcriptRef.current, partial = partialTextRef.current, manual = false) => {
    const content = `${transcriptText || ''}\n${partial || ''}`.trim();
    if (!selectedModelId) {
      if (manual) messageApi.warning('请先选择分析模型，小助手才能生成实时提醒');
      return;
    }
    if (!content || content.length < 12) {
      const next = { type: 'thinking' as const, message: '我正在听，内容再完整一点就提醒你。', createdAt: Date.now() };
      setLiveInsight(next);
      return;
    }
    const now = Date.now();
    if (!manual && now - lastInsightAtRef.current < 25000) return;
    lastInsightAtRef.current = now;
    setLiveInsightLoading(true);
    setLiveInsight({ type: 'thinking', message: manual ? '我正在快速看这段会议内容。' : '我在归纳刚刚这段会议重点。', createdAt: now });
    try {
      const res = await api.post('/ai-demand-center/meeting/live-insight', {
        transcript: transcriptText.slice(-3000),
        partialText: partial.slice(-800),
        modelId: selectedModelId,
        recentInterjections: recentInterjectTitlesRef.current.slice(-8),
      });
      const type = ['speaking', 'alert', 'knowledge', 'thinking', 'error'].includes(res.data?.type) ? res.data.type : 'speaking';
      setLiveInsight({ type, message: res.data?.message || '我已捕捉到新的会议要点。', createdAt: Date.now() });
      const interject = res.data?.interject;
      if (interject?.speech && interject?.title) void recordMascotInterjection(interject);
    } catch (error: any) {
      setLiveInsight({ type: 'error', message: error?.response?.data?.message || '实时提醒暂时不可用。', createdAt: Date.now() });
      if (manual) messageApi.error(error?.response?.data?.message || '实时提醒失败');
    } finally {
      setLiveInsightLoading(false);
    }
  };

  /** 小灵想插嘴：立刻落库留档（不管用户之后确认还是取消），再弹出友好询问 */
  const recordMascotInterjection = async (interject: { category?: string; title: string; speech: string; suggestion?: string; evidence?: string }) => {
    try {
      // 会议进行中任务一定已创建；万一没有（如导入场景），先建任务保证记录归属到会议任务
      const taskId = currentTaskIdRef.current || await ensureCurrentTask();
      const res = await api.post('/ai-demand-center/mascot-insights', { taskId, ...interject });
      recentInterjectTitlesRef.current = [...recentInterjectTitlesRef.current, interject.title].slice(-8);
      // 已有待回应的询问时不打断（旧记录保持 pending，可在"小灵笔记"里查看），只替换展示最新一条
      setMascotInterjection(res.data);
      mascotInterjectionRef.current = res.data;
    } catch (error) {
      console.warn('[XIAOLING_INTERJECT] save failed', error);
    }
  };

  /** 用户回应小灵的插嘴询问：确认(spoken)/取消(dismissed)，记录状态都更新到会议任务 */
  const respondMascotInterjection = async (accept: boolean) => {
    const current = mascotInterjectionRef.current;
    if (!current) return;
    setMascotInterjection(null);
    mascotInterjectionRef.current = null;
    try {
      await api.patch(`/ai-demand-center/mascot-insights/${current.id}`, { status: accept ? 'spoken' : 'dismissed' });
    } catch (error) {
      console.warn('[XIAOLING_INTERJECT] update status failed', error);
    }
  };

  const hydrateTaskArtifacts = (storedArtifacts: any[]) => {
    const baseArtifacts = INITIAL_ARTIFACTS;
    if (!Array.isArray(storedArtifacts)) {
      setMeetingArtifactRequirements([]);
      setArtifacts(baseArtifacts);
      return;
    }
    const requirementHolder = storedArtifacts.find((artifact: any) => artifact?.id === MEETING_ARTIFACT_REQUIREMENTS_ID);
    const requirements: MeetingArtifactRequirement[] = Array.isArray(requirementHolder?.meetingArtifactRequirements)
      ? requirementHolder.meetingArtifactRequirements
      : Array.isArray(requirementHolder?.content) ? requirementHolder.content : [];
    const meetingItems = requirements.map(buildMeetingArtifactItem);
    setMeetingArtifactRequirements(requirements);
    const persistedIds = storedArtifacts
      .filter((artifact: any) => artifact?.id && artifact?.id !== MEETING_ARTIFACT_REQUIREMENTS_ID && artifact?.content)
      .map((artifact: any) => String(artifact.id));
    if (persistedIds.length || meetingItems.length) {
      setSelectedArtifactIds(prev => Array.from(new Set([...prev, ...persistedIds, ...meetingItems.map(item => item.id)])));
    }
    const allBase = [...baseArtifacts, ...meetingItems];
    setArtifacts(allBase.map(item => {
      const stored = storedArtifacts.find((artifact: any) => artifact?.id === item.id);
      if (!stored) return item;
      const isDone = stored.status === 'confirmed' || stored.status === 'review';
      let pptProjectId = '';
      try { const parsed = JSON.parse(stored.content || '{}'); if (parsed.pptProjectId) pptProjectId = parsed.pptProjectId; } catch {}
      return {
        ...item,
        status: stored.status || 'review',
        progress: isDone ? 100 : (stored.progress || 0),
        content: stored.content || item.content,
        pptProjectId,
        preview: pptProjectId ? `PPT项目 · ${JSON.parse(stored.content||'{}').slideCount || 0} 页` : (stored.content ? String(stored.content).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').slice(0, 120) : item.preview),
        references: stored.references,
        generatedAt: stored.generatedAt,
        fileSize: stored.fileSize,
        meetingRequirement: stored.meetingRequirement || item.meetingRequirement,
        step: stored.status === 'confirmed' ? '已确认' : stored.status === 'ppt_review' ? '待生图确认' : isDone ? '已生成，等待确认' : (stored.step || ''),
        steps: isDone ? [] : (stored.steps?.length ? stored.steps : []),
      };
    }));
  };

  const loadTask = async (taskId: string) => {
    setTaskLoading(true);
    try {
      const res = await api.get(`/ai-demand-center/tasks/${taskId}`);
      const task = res.data;
      setCurrentTaskId(task.id);
      currentTaskIdRef.current = task.id;
      setTranscript(task.transcript || '');
      transcriptRef.current = task.transcript || '';
      lastSavedTranscriptRef.current = task.transcript || '';
      setPartialText('');
      partialTextRef.current = '';
      setAnalysis(task.analysis || '');
      setAnalysisError('');
      setTaskName(task.name || '');
      setIntelligence(task.intelligence || { enabled: true, status: 'idle', sources: [] });
      hydrateTaskArtifacts(task.artifacts || []);
      if (task.modelId) setSelectedModelId(task.modelId);
    } catch {
      messageApi.error('加载任务失败');
    } finally {
      setTaskLoading(false);
    }
  };

  const handleNewTask = () => {
    setCurrentTaskId('');
    currentTaskIdRef.current = '';
    setTranscript('');
    transcriptRef.current = '';
    setPartialText('');
    partialTextRef.current = '';
    lastSavedTranscriptRef.current = '';
    setAnalysis('');
    setAnalysisError('');
    setTaskName('');
    setIntelligence({ enabled: true, status: 'idle', sources: [] });
    setMeetingArtifactRequirements([]);
    setArtifacts(INITIAL_ARTIFACTS);
  };

  const cleanupAudio = () => {
    processorRef.current?.disconnect();
    processorRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
  };

  const bindSocketEvents = (socket: Socket) => {
    socket.on('ai-demand:asr-ready', () => setRecordStatus('recording'));
    socket.on('ai-demand:asr-text', (payload: { text?: string; isFinal?: boolean; segId?: number }) => {
      const text = payload?.text?.trim();
      if (!text) return;
      if (payload.isFinal) {
        appendFinalTranscript(text);
      } else {
        setPartialText(text);
        partialTextRef.current = text;
      }
    });
    socket.on('ai-demand:asr-error', (payload: { message?: string }) => {
      setRecordStatus('error');
      messageApi.error(payload?.message || '实时转写失败');
    });
    socket.on('disconnect', () => {
      if (recordStatus === 'recording' || recordStatus === 'connecting') setRecordStatus('idle');
    });
  };

  const startRecording = async () => {
    if (!authUser?.id) { messageApi.warning('请先登录后再录音'); return; }
    if (!navigator?.mediaDevices?.getUserMedia) {
      messageApi.error('当前浏览器不支持麦克风。请使用 HTTPS 域名（如 https://etc.anhui-ai-agent.com）访问此页面，录音必须通过安全连接。');
      return;
    }
    setRecordStatus('connecting');
    setPartialText('');
    partialTextRef.current = '';
    try {
      await ensureCurrentTask();
      const socket = io('/ws', { auth: { token: useAuthStore.getState().token } });
      socketRef.current = socket;
      bindSocketEvents(socket);
      socket.emit('ai-demand:asr-start');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, noiseSuppression: true, echoCancellation: true } });
      streamRef.current = stream;
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      processor.onaudioprocess = async event => {
        const socketNow = socketRef.current;
        if (!socketNow?.connected) return;
        const pcm = await float32ToPcm16k(event.inputBuffer.getChannelData(0), audioContext.sampleRate);
        socketNow.emit('ai-demand:asr-audio', { audio: arrayBufferToBase64(pcm.buffer) });
      };
      source.connect(processor);
      processor.connect(audioContext.destination);
      messageApi.success('已开始录音，转写文字会实时显示在页面上');
    } catch (error: any) {
      cleanupAudio();
      socketRef.current?.disconnect();
      socketRef.current = null;
      setRecordStatus('error');
      if (error?.name === 'NotAllowedError') {
        messageApi.error('麦克风权限被拒绝，请在浏览器设置中允许麦克风访问');
      } else {
        messageApi.error(error?.message || '无法启动麦克风录音');
      }
    }
  };

  const stopRecording = async (runAnalysis = false) => {
    if (recordStatus === 'idle') return;
    setRecordStatus('stopping');
    cleanupAudio();
    socketRef.current?.emit('ai-demand:asr-stop');
    socketRef.current?.disconnect();
    socketRef.current = null;
    const latestPartial = partialTextRef.current.trim();
    const text = latestPartial ? appendFinalTranscript(latestPartial).trim() : transcriptRef.current.trim();
    setRecordStatus('idle');
    if (!text.trim()) { messageApi.warning('没有录制到音频内容'); return; }

    // 暂停/结束时强制保存一次全文，保证刷新后能恢复
    try {
      const taskId = await ensureCurrentTask();
      await api.put(`/ai-demand-center/tasks/${taskId}`, { transcript: text, modelId: selectedModelId, status: 'recorded' });
      lastSavedTranscriptRef.current = text;
      setDemandTasks(prev => prev.map(item => item.id === taskId ? { ...item, status: item.status === 'analyzed' ? item.status : 'recorded' } : item));
      messageApi.success('录音全文已保存，可继续录音或进行 AI 提炼');
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || '保存转写失败');
    }

    if (runAnalysis && selectedModelId) {
      setActiveTab('analysis');
      await analyzeTranscript(text);
    } else if (runAnalysis) {
      messageApi.info('录音已保存。请选择分析模型后，点击“AI提炼会议内容”。');
    }
  };

  const handleMeetingFileImport = async (file: File) => {
    const fileName = file.name || '导入文件';
    if (!/\.(doc|docx|txt)$/i.test(fileName)) {
      messageApi.warning('仅支持导入 doc、docx、txt 文件');
      return false;
    }
    if (recordStatus === 'recording' || recordStatus === 'connecting' || recordStatus === 'stopping') {
      messageApi.warning('请先暂停并保存当前录音，再导入文件');
      return false;
    }
    const formData = new FormData();
    formData.append('file', file);
    if (currentTaskIdRef.current) formData.append('taskId', currentTaskIdRef.current);
    if (selectedModelId) formData.append('modelId', selectedModelId);
    formData.append('name', `导入纪要-${fileName.replace(/\.[^.]+$/, '')}`);
    setMeetingFileImporting(true);
    try {
      const res = await api.post('/ai-demand-center/meeting/import-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const data = res.data || {};
      const text = String(data.transcript || '').trim();
      if (!text) {
        messageApi.warning('文件已上传，但没有解析到可用文字');
        return false;
      }
      setTranscript(text);
      transcriptRef.current = text;
      lastSavedTranscriptRef.current = text;
      setPartialText('');
      partialTextRef.current = '';
      setAnalysis('');
      setAnalysisError('');
      setMeetingArtifactRequirements([]);
      setArtifacts(INITIAL_ARTIFACTS);
      if (data.taskId) {
        setCurrentTaskId(data.taskId);
        currentTaskIdRef.current = data.taskId;
      }
      if (data.taskName) setTaskName(data.taskName);
      await fetchTasks();
      setActiveTab('meeting');
      messageApi.success(`已导入 ${data.fileName || fileName}，解析 ${data.charCount || text.length} 字，可直接生成会议纪要`);
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || error?.message || '文件导入失败');
    } finally {
      setMeetingFileImporting(false);
    }
    return false;
  };

  const analyzeTranscript = async (text?: string) => {
    const content = (text || transcriptRef.current).trim();
    if (!content.trim()) { messageApi.warning('还没有可分析的转写文字'); return; }
    if (!selectedModelId) { messageApi.warning('转写文字已保存，请先选择分析模型'); return; }
    setActiveTab('analysis');
    setAnalysisError('');
    setAnalysisLoading(true);
    const quickKeywords = content.match(/[\u4e00-\u9fa5A-Za-z0-9]{2,18}/g)?.slice(0, 6) || [];
    setIntelligence({ enabled: true, status: 'keywording', keywords: quickKeywords, sources: [], injected: false });
    let analyzeProgressTimer: number | undefined;
    try {
      const taskId = await ensureCurrentTask();
      await saveTranscriptDraft(content, true);
      setIntelligence(prev => ({ ...prev, status: 'searching' }));
      analyzeProgressTimer = window.setTimeout(() => {
        setIntelligence(prev => (
          prev.status === 'searching'
            ? { ...prev, status: 'analyzing', injected: Boolean(prev.injected) }
            : prev
        ));
      }, 12000);
      const res = await api.post('/ai-demand-center/meeting/analyze', {
        transcript: content,
        modelId: selectedModelId,
        taskId,
        mode: analysisMode,
      }, { timeout: 610000 });
      window.clearTimeout(analyzeProgressTimer);
      analyzeProgressTimer = undefined;
      setAnalysis(res.data?.content || '');
      if (Array.isArray(res.data?.meetingArtifactRequirements)) {
        const requirements = res.data.meetingArtifactRequirements as MeetingArtifactRequirement[];
        setMeetingArtifactRequirements(requirements);
        setArtifacts(prev => [...prev.filter(item => !item.meetingRequirement), ...requirements.map(buildMeetingArtifactItem)]);
        setSelectedArtifactIds(prev => Array.from(new Set([...prev, ...requirements.map(req => meetingRequirementArtifactId(req.id))])));
      }
      setIntelligence(res.data?.intelligence || { enabled: true, status: 'empty', sources: [], injected: false });
      if (res.data?.taskName) setTaskName(res.data.taskName);
      if (res.data?.taskId && !currentTaskId) {
        setCurrentTaskId(res.data.taskId);
        currentTaskIdRef.current = res.data.taskId;
        fetchTasks();
      }
      messageApi.success('会议纪要已生成');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'AI分析失败';
      setAnalysisError(message);
      setIntelligence(prev => ({ ...prev, status: 'analysis_failed', error: undefined, injected: Boolean(prev.injected) }));
      messageApi.error(message);
    } finally {
      if (analyzeProgressTimer) window.clearTimeout(analyzeProgressTimer);
      setAnalysisLoading(false);
    }
  };

  const exportMeetingMinutes = async (format: 'docx' | 'pdf') => {
    const exportHtml = formatMinutesHtml(
      analysis,
      taskName,
      ANALYSIS_MODES.find(item => item.key === analysisMode)?.name || '通用标准版',
    );
    if (!exportHtml) {
      messageApi.warning('请先生成会议纪要再导出');
      return;
    }
    const token = useAuthStore.getState().token || '';
    if (!token) {
      messageApi.error('登录状态已失效，请重新登录后导出');
      return;
    }

    const key = `minutes-export-${format}`;
    const formatName = format === 'docx' ? 'Word' : 'PDF';
    setMinutesExporting(format);
    messageApi.loading({ key, content: `正在排版并生成 ${formatName} 文件…`, duration: 0 });
    try {
      await downloadOfficeFile(
        exportHtml,
        buildMinutesExportName(taskName),
        format,
        token,
        msg => { messageApi.success({ key, content: msg }); },
        error => { messageApi.error({ key, content: `导出失败：${error}` }); },
      );
    } finally {
      setMinutesExporting(null);
    }
  };

  const formatLocalDateTimeInput = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  const parseLocalDateTimeInput = (value: string) => {
    const [datePart, timePart = '00:00'] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    return new Date(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, 0, 0);
  };
  const addMinutesToLocalInput = (value: string, minutes: number) => formatLocalDateTimeInput(new Date(parseLocalDateTimeInput(value).getTime() + minutes * 60000));
  const toTencentIso = (value: string) => value ? `${value}:00+08:00` : '';
  const formatTencentTime = (value?: string) => value ? value.replace('T', ' ').replace(/:00\+08:00$/, '').replace(/\+08:00$/, '') : '-';
  const getDurationText = (start?: string, end?: string) => {
    if (!start || !end) return '-';
    const diff = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
    if (!Number.isFinite(diff) || diff <= 0) return '-';
    if (diff < 60) return `${diff} 分钟`;
    if (diff % 60 === 0) return `${diff / 60} 小时`;
    return `${Math.floor(diff / 60)}小时${diff % 60}分钟`;
  };
  const copyTencentText = async (text: string, label: string) => {
    if (!text) { messageApi.warning(`${label}为空，无法复制`); return; }
    await navigator.clipboard?.writeText(text);
    messageApi.success(`已复制${label}`);
  };
  const extractTencentMeetingId = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') return '';
    if (Array.isArray(value)) {
      for (const item of value) {
        const id = extractTencentMeetingId(item);
        if (id) return id;
      }
      return '';
    }
    if (typeof value !== 'object') return '';
    const direct = value.meeting_id || value.meetingId || value.id;
    if (direct) return String(direct).trim();
    for (const key of ['meeting_info', 'meetingInfo', 'meeting', 'data', 'result', 'response', 'meeting_info_list']) {
      const id = extractTencentMeetingId(value[key]);
      if (id) return id;
    }
    return '';
  };
  const getTencentMeetingId = (meeting: TencentMeetingItem) => extractTencentMeetingId(meeting);
  const getTencentMeetingTitle = (meeting: TencentMeetingItem) => String(meeting.subject || meeting.title || meeting.meeting_name || meeting.meeting_code || getTencentMeetingId(meeting) || '未命名会议');
  const getTencentMeetingLabel = (meeting: TencentMeetingItem) => {
    const code = String(meeting.meeting_code || (meeting as any).meetingCode || '').trim();
    const start = meeting.start_time || (meeting as any).startTime || '';
    const end = meeting.end_time || (meeting as any).endTime || '';
    const fmt = (t: string) => {
      if (!t) return '';
      try {
        const d = new Date(t);
        if (isNaN(d.getTime())) return '';
        return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
      } catch { return ''; }
    };
    const startStr = fmt(start) || '-';
    const endStr = fmt(end) || '-';
    return `${code || '无会议号'}  ${startStr} ～ ${endStr}`;
  };
  const getTencentMeetingTimeMs = (meeting: TencentMeetingItem, key: 'start' | 'end') => {
    const value = key === 'start' ? (meeting.start_time || meeting.startTime) : (meeting.end_time || meeting.endTime);
    const ms = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(ms) ? ms : 0;
  };
  const pickNearestUpcomingTencentMeeting = (meetings: TencentMeetingItem[]) => {
    const now = Date.now();
    return [...meetings].sort((a, b) => {
      const aStart = getTencentMeetingTimeMs(a, 'start');
      const bStart = getTencentMeetingTimeMs(b, 'start');
      const aFuture = aStart >= now;
      const bFuture = bStart >= now;
      if (aFuture !== bFuture) return aFuture ? -1 : 1;
      return aFuture ? aStart - bStart : bStart - aStart;
    })[0];
  };
  const pickLatestEndedTencentMeeting = (meetings: TencentMeetingItem[]) => [...meetings].sort((a, b) => {
    const endDiff = getTencentMeetingTimeMs(b, 'end') - getTencentMeetingTimeMs(a, 'end');
    return endDiff || (getTencentMeetingTimeMs(b, 'start') - getTencentMeetingTimeMs(a, 'start'));
  })[0];
  const rememberRecentTencentMeeting = (meeting: TencentMeetingItem) => {
    const id = getTencentMeetingId(meeting);
    if (!id) return;
    persistMeetingCodeFromItem(meeting);
    setRecentTencentMeetings(prev => [meeting, ...prev.filter(item => getTencentMeetingId(item) !== id)].slice(0, 5));
  };
  const selectableTencentMeetings = useMemo(() => {
    const merged = [...tencentCurrentMeetings, ...recentTencentMeetings, ...tencentMeetings];
    const seen = new Set<string>();
    const unique = merged.filter(item => {
      const id = getTencentMeetingId(item);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    // 按开始时间倒序（最新的在最前面）
    unique.sort((a, b) => {
      const aTime = getTencentMeetingTimeMs(a, 'start');
      const bTime = getTencentMeetingTimeMs(b, 'start');
      return bTime - aTime || (getTencentMeetingTimeMs(b, 'end') - getTencentMeetingTimeMs(a, 'end'));
    });
    return unique;
  }, [tencentCurrentMeetings, recentTencentMeetings, tencentMeetings]);

  const fetchTencentMeetingStatus = async () => {
    setTencentStatusLoading(true);
    try {
      const res = await api.get('/ai-demand-center/tencent-meeting/status');
      const authorized = !!res.data?.authorized;
      setTencentMeetingStatus({ authorized, message: res.data?.message || '' });
      if (authorized) {
        await fetchTencentCurrentMeetings(true);
      } else {
        messageApi.warning(res.data?.message || '腾讯会议 CLI 未授权，请在服务器执行 tmeet auth login');
      }
    } catch (error: any) {
      setTencentMeetingStatus({ authorized: false, message: error?.response?.data?.message || '腾讯会议 CLI 状态检查失败' });
      messageApi.error(error?.response?.data?.message || '腾讯会议 CLI 状态检查失败');
    } finally {
      setTencentStatusLoading(false);
    }
  };

  const stopTmeetAuthPolling = () => {
    if (tmeetAuthPollRef.current) { window.clearInterval(tmeetAuthPollRef.current); tmeetAuthPollRef.current = null; }
  };

  const startTmeetAuthLogin = async () => {
    setTmeetAuthModalOpen(true);
    setTmeetAuthPhase('starting');
    setTmeetAuthUrl('');
    setTmeetAuthError('');
    stopTmeetAuthPolling();
    try {
      const res = await api.post('/ai-demand-center/tencent-meeting/auth/login', {}, { timeout: 60000 });
      const { status, authUrl, error } = res.data || {};
      if (status === 'pending' && authUrl) {
        setTmeetAuthUrl(authUrl);
        setTmeetAuthPhase('waiting');
        tmeetAuthPollRef.current = window.setInterval(async () => {
          try {
            const state = await api.get('/ai-demand-center/tencent-meeting/auth/login-state');
            if (state.data?.status === 'success') {
              stopTmeetAuthPolling();
              setTmeetAuthPhase('success');
              messageApi.success('腾讯会议授权成功');
              fetchTencentMeetingStatus();
            } else if (state.data?.status === 'failed') {
              stopTmeetAuthPolling();
              setTmeetAuthPhase('failed');
              setTmeetAuthError(state.data?.error || '授权失败，请重试');
            }
          } catch {}
        }, 3000);
      } else {
        setTmeetAuthPhase('failed');
        setTmeetAuthError(error || '发起授权失败，请重试');
      }
    } catch (error: any) {
      setTmeetAuthPhase('failed');
      setTmeetAuthError(error?.response?.data?.message || '发起授权失败，请重试');
    }
  };

  const openTmeetAuth = () => {
    if (tencentMeetingStatus?.authorized) {
      Modal.confirm({
        title: '重新授权腾讯会议',
        content: '当前 CLI 已授权可用。重新授权会先退出现有登录，完成前腾讯会议功能短暂不可用，确定继续吗？',
        okText: '重新授权',
        cancelText: '取消',
        onOk: startTmeetAuthLogin,
      });
    } else {
      startTmeetAuthLogin();
    }
  };

  const closeTmeetAuthModal = () => {
    stopTmeetAuthPolling();
    setTmeetAuthModalOpen(false);
    fetchTencentMeetingStatus();
  };

  useEffect(() => () => stopTmeetAuthPolling(), []);

  const fetchTencentCurrentMeetings = async (autoOpenFirst = false) => {
    setTencentMeetingLoading(true);
    try {
      const res = await api.get('/ai-demand-center/tencent-meeting/current', { timeout: 100000 });
      const meetings = Array.isArray(res.data?.meetings) ? res.data.meetings : [];
      setTencentCurrentMeetings(meetings);
      if (meetings.length) {
        const target = pickNearestUpcomingTencentMeeting(meetings);
        const targetId = getTencentMeetingId(target);
        if (autoOpenFirst && targetId) {
          setTencentSelectedMeetingId(targetId);
          await fetchTencentMeetingInfoById(targetId, true);
        }
        messageApi.success(`已自动加载 ${meetings.length} 个当前/待开始腾讯会议`);
        return;
      }

      if (autoOpenFirst) {
        const codes = loadPersistedMeetingCodes();
        const params: any = { timeout: 100000 };
        if (codes.length) params.params = { meetingCodes: codes.join(',') };
        const endedRes = await api.get('/ai-demand-center/tencent-meeting/meetings', params);
        const endedMeetings = Array.isArray(endedRes.data?.meetings) ? endedRes.data.meetings : [];
        setTencentMeetings(endedMeetings);
        endedMeetings.forEach(m => persistMeetingCodeFromItem(m));
        const latestEnded = pickLatestEndedTencentMeeting(endedMeetings);
        const latestEndedId = latestEnded ? getTencentMeetingId(latestEnded) : '';
        if (latestEndedId) {
          setTencentSelectedMeetingId(latestEndedId);
          await fetchTencentMeetingInfoById(latestEndedId, true);
          messageApi.info('当前没有待开始会议，已自动展示最近一次历史会议');
        } else {
          messageApi.info('未查询到当前/待开始会议，也没有最近历史会议；可手动输入会议ID/会议号');
        }
      }
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || error?.message || '查询当前腾讯会议失败');
    } finally {
      setTencentMeetingLoading(false);
    }
  };

  const fetchTencentMeetings = async () => {
    setTencentMeetingLoading(true);
    try {
      const codes = loadPersistedMeetingCodes();
      const params: any = { timeout: 100000 };
      if (codes.length) params.params = { meetingCodes: codes.join(',') };
      const res = await api.get('/ai-demand-center/tencent-meeting/meetings', params);
      const meetings = Array.isArray(res.data?.meetings) ? res.data.meetings : [];
      setTencentMeetings(meetings);
      // 成功获取到的会议的 meeting_code 也持久化
      meetings.forEach(m => persistMeetingCodeFromItem(m));
      if (!meetings.length) messageApi.info('最近 7 天未查询到腾讯会议，可手动填写会议ID/录制ID');
      else messageApi.success(`已加载 ${meetings.length} 个会议`);
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || error?.message || '查询腾讯会议失败');
    } finally {
      setTencentMeetingLoading(false);
    }
  };

  const createTencentMeeting = async () => {
    if (!tencentSubject.trim()) { messageApi.warning('请填写会议主题'); return; }
    if (!tencentStart) { messageApi.warning('请填写会议开始时间'); return; }
    setTencentCreateLoading(true);
    try {
      const startIso = toTencentIso(tencentStart);
      const endIso = toTencentIso(addMinutesToLocalInput(tencentStart, tencentDuration));
      const res = await api.post('/ai-demand-center/tencent-meeting/create', {
        subject: tencentSubject.trim(),
        start: startIso,
        end: endIso,
        autoRecord: true,
        autoAsr: true,
      }, { timeout: 100000 });
      const meeting = res.data?.meeting || {};
      const id = extractTencentMeetingId(meeting || res.data);
      if (id) {
        rememberRecentTencentMeeting(meeting || res.data);
        setTencentSelectedMeetingId(id);
        fetchTencentMeetingInfoById(id);
        messageApi.success('会议已创建，可在下拉框随时切回这个新会议');
      }
      else messageApi.warning('会议已创建，正在查询详情...');
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || error?.message || '创建腾讯会议失败');
    } finally {
      setTencentCreateLoading(false);
    }
  };

  const createQuickMeeting = async () => {
    setTencentCreateLoading(true);
    try {
      const now = new Date(Date.now() + 60000);
      const startStr = formatLocalDateTimeInput(now);
      const startIso = toTencentIso(startStr);
      const endIso = toTencentIso(addMinutesToLocalInput(startStr, 60));
      const res = await api.post('/ai-demand-center/tencent-meeting/create', {
        subject: `快速会议 ${now.toLocaleString('zh-CN', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', hour12:false })}`,
        start: startIso,
        end: endIso,
        autoRecord: true,
        autoAsr: true,
      }, { timeout: 100000 });
      const meeting = res.data?.meeting || {};
      const id = extractTencentMeetingId(meeting || res.data);
      if (id) {
        rememberRecentTencentMeeting(meeting || res.data);
        setTencentSelectedMeetingId(id);
        fetchTencentMeetingInfoById(id);
        messageApi.success('快速会议已创建，可在下拉框随时切回这个新会议');
      } else {
        messageApi.warning('快速会议已创建，正在查询详情...');
      }
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || error?.message || '快速会议创建失败');
    } finally {
      setTencentCreateLoading(false);
    }
  };

  const fetchTencentMeetingInfoById = async (id: string, silent = false) => {
    setTencentMeetingInfoLoading(true);
    try {
      const res = await api.get(`/ai-demand-center/tencent-meeting/${encodeURIComponent(id)}/info`, { timeout: 30000 });
      const data = res.data || null;
      setTencentMeetingInfo(data);
      const recordId = extractTencentRecordId(data);
      if (recordId) {
        setTencentRecordId(recordId);
        if (!silent) messageApi.success('已通过腾讯会议接口自动获取录制ID');
      } else if (data?.recordError) {
        if (!silent) messageApi.warning(`会议详情已加载，但录制列表查询失败：${data.recordError}`);
      } else {
        setTencentRecordId('');
        if (!silent) messageApi.info('会议详情已加载，暂未查询到录制ID；请确认会议已结束并已生成云录制');
      }
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || error?.message || '加载会议信息失败');
      setTencentMeetingInfo(null);
    } finally {
      setTencentMeetingInfoLoading(false);
    }
  };

  const fetchTencentMeetingInfo = async () => {
    const id = tencentSelectedMeetingId.trim();
    if (!id) { messageApi.warning('请先选择或填写会议ID/会议号'); return; }
    await fetchTencentMeetingInfoById(id);
  };

  const extractTencentRecordId = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') return '';
    if (Array.isArray(value)) {
      for (const item of value) {
        const id = extractTencentRecordId(item);
        if (id) return id;
      }
      return '';
    }
    if (typeof value !== 'object') return '';
    const direct = value.record_file_id || value.recordFileId || value.record_id || value.recordId || value.id;
    if (direct) return String(direct).trim();
    for (const key of ['record_file_list', 'recordFileList', 'records', 'record_list', 'items', 'list', 'data', 'result']) {
      const id = extractTencentRecordId(value[key]);
      if (id) return id;
    }
    return '';
  };

  const extractTencentMeeting = async () => {
    if (!tencentSelectedMeetingId.trim()) {
      messageApi.warning('请先选择已结束会议，系统会自动通过腾讯会议接口查询录制ID');
      return;
    }
    setTencentExtractLoading(true);
    try {
      const res = await api.post('/ai-demand-center/tencent-meeting/extract', {
        meetingId: tencentSelectedMeetingId.trim(),
        recordId: tencentRecordId.trim(),
        mode: tencentExtractMode,
        modelId: selectedModelId,
        taskId: currentTaskIdRef.current || undefined,
        name: taskName || tencentSubject || undefined,
      }, { timeout: 120000 });
      const data = res.data || {};
      if (data.taskId) {
        setCurrentTaskId(data.taskId);
        currentTaskIdRef.current = data.taskId;
      }
      if (data.taskName) setTaskName(data.taskName);
      if (data.transcript) {
        setTranscript(data.transcript);
        transcriptRef.current = data.transcript;
        lastSavedTranscriptRef.current = data.transcript;
      }
      if (data.analysis) {
        setAnalysis(data.analysis);
        setActiveTab('analysis');
      }
      await fetchTasks();
      messageApi.success(tencentExtractMode === 'minutes' ? '已提取腾讯会议智能纪要，可直接生成产物' : '已提取腾讯会议转写内容，可继续生成会议纪要');
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || error?.message || '提取腾讯会议数据失败');
    } finally {
      setTencentExtractLoading(false);
    }
  };

  const renderIntelligencePanel = () => {
    const statusText = intelligence.status === 'idle' ? '等待会议转写完成后检索网络情报'
      : intelligence.status === 'keywording' ? '正在从会议内容中提取检索关键词...'
      : intelligence.status === 'searching' ? '正在检索公开网络情报...'
      : intelligence.status === 'analyzing' ? '网络情报检索已结束，AI正在生成会议纪要...'
      : intelligence.status === 'analysis_failed' ? '网络情报检索已结束，AI生成会议纪要失败。'
      : intelligence.status === 'done' ? `已命中 ${intelligence.sources.length} 条网络情报，作为低置信度候选材料交由AI判断是否采用`
      : intelligence.status === 'empty' ? '未检索到高相关网络情报，本次分析仅基于会议内容和本地资料生成。'
      : '网络情报检索失败，已跳过，不影响会议分析。';
    const loading = intelligence.status === 'keywording' || intelligence.status === 'searching' || intelligence.status === 'analyzing';
    return (
      <div className={`adc-intel-panel adc-intel-${intelligence.status}`}>
        <div className="adc-intel-head">
          <div>
            <strong>网络情报检索面板</strong>
            <p>{statusText}</p>
          </div>
          <Space>
            {loading ? <Tag color="processing" icon={<LoadingOutlined />}>检索中</Tag> : null}
            {intelligence.injected ? <Tag color="green">已注入AI分析</Tag> : <Tag>未注入</Tag>}
          </Space>
        </div>
        {intelligence.query ? <div className="adc-intel-query"><span>检索词</span><code>{intelligence.query}</code><em>超时 10s · 最多 5 条</em></div> : null}
        {intelligence.keywords?.length ? <div className="adc-intel-keywords">{intelligence.keywords.map(keyword => <Tag key={keyword} color="cyan">{keyword}</Tag>)}</div> : null}
        {intelligence.error && intelligence.status === 'failed' ? <Alert type="warning" showIcon message="网络情报检索失败" description={intelligence.error} /> : null}
        {intelligence.sources.length ? <div className="adc-intel-source-list">
          {intelligence.sources.map(source => (
            <article className="adc-intel-source" key={source.id}>
              <div className="adc-intel-source-top">
                <strong>{source.title}</strong>
                <Tag color="blue">{source.score || 0}%</Tag>
              </div>
              <a href={source.url} target="_blank" rel="noreferrer">{source.domain || source.url}</a>
              <p>{source.summary}</p>
              <div className="adc-intel-source-foot">
                <span>{source.reason || '公开网络来源'}</span>
                <span>{new Date(source.fetchedAt).toLocaleString()}</span>
                <Button size="small" onClick={() => navigator.clipboard?.writeText(`${source.title}\n${source.url}\n${source.summary}`)}>复制摘要</Button>
              </div>
            </article>
          ))}
        </div> : null}
      </div>
    );
  };

  const handleFakeUpload = async () => {
    if (!selectedFiles.length) {
      messageApi.warning('请先选择要放入共创资料夹的文件');
      return;
    }
    const parsed = await Promise.all(selectedFiles.map(async (file, index) => {
      const raw = file.originFileObj as File | undefined;
      let content = '';
      if (raw && /\.(txt|md|json|csv|log)$/i.test(raw.name)) {
        content = await raw.text().catch(() => '');
      }
      return {
        id: `upload-${Date.now()}-${index}`,
        name: file.name,
        kind: raw?.type || file.name.split('.').pop()?.toUpperCase() || '文件',
        meta: content ? `${Math.round((raw?.size || 0) / 1024)}KB · 已读取 ${content.length} 字` : `${Math.round((raw?.size || 0) / 1024)}KB · 已登记，生成时按文件名引用`,
        status: content ? '已解析' : '待确认',
        selected: true,
        content,
      } as MaterialItem;
    }));
    setMaterials(prev => [...prev, ...parsed]);
    messageApi.success(`${parsed.length} 个文件已放入当前任务的“AI需求共创资料夹”`);
    setUploadOpen(false);
    setSelectedFiles([]);
  };

  const generateSingleArtifact = async (item: ArtifactItem, adjustMode = false) => {
    if (!selectedModelId) { messageApi.warning('请先在“会议录音”页选择分析模型，再生成产物'); return; }
    // 问题10根因修复：会议需求产物卡片可能传入不在 artifacts 状态里的新对象，
    // 之前 setArtifacts(prev.map(...)) 无法插入新项，导致点击后状态更新全部落空、页面无任何反馈。
    // 这里先把产物 upsert 进状态列表，并确保其被勾选，让“生成进度”卡片一定会出现。
    setSelectedArtifactIds(prev => prev.includes(item.id) ? prev : [...prev, item.id]);
    setArtifacts(prev => {
      const base = prev.some(artifact => artifact.id === item.id) ? prev : [...prev, item];
      return base.map(artifact => artifact.id === item.id ? { ...artifact, status: 'running', progress: 8, error: undefined, step: adjustMode ? '正在读取上次结果并按补充说明调整' : GENERATION_STEPS[1], steps: adjustMode ? ['已进入调整队列', '正在读取上次结果并按补充说明调整'] : [GENERATION_STEPS[0], GENERATION_STEPS[1]] } : artifact);
    });
    const timer = window.setInterval(() => {
      setArtifacts(prev => prev.map(artifact => artifact.id === item.id && artifact.status === 'running'
        ? (() => { const nextProgress = Math.min((artifact.progress || 8) + Math.floor(Math.random() * 9) + 4, 88); const stepIndex = nextProgress < 28 ? 1 : nextProgress < 52 ? 2 : nextProgress < 78 ? 3 : 4; const nextStep = GENERATION_STEPS[stepIndex]; return { ...artifact, progress: nextProgress, step: nextStep, steps: Array.from(new Set([...(artifact.steps || []), nextStep])) }; })()
        : artifact));
    }, 900);
    try {
      const res = await api.post('/ai-demand-center/artifacts/generate', {
        modelId: selectedModelId,
        taskId: currentTaskIdRef.current,
        taskName: taskName || '未命名共创任务',
        minutesHtml: analysis,
        artifact: { id: item.id, name: item.name, format: item.format, desc: item.desc, meetingRequirement: item.meetingRequirement }, extraNotes: artifactNotes[item.id] || '', deadline: item.id === 'timeline' ? timelineDeadline : '',
        previousContent: adjustMode ? (item.content || '') : '',
        adjustMode,
        materials: selectedMaterials.map(m => ({ id: m.id, name: m.name, kind: m.kind, content: m.content || m.meta })),
        useKnowledgeBase,
      }, { timeout: 220000 });
      const content = res.data?.content || '';
      const pptProjectId = res.data?.pptProjectId || '';
      setArtifacts(prev => prev.map(artifact => artifact.id === item.id ? {
        ...artifact,
        name: res.data?.name || artifact.name,
        status: item.format === 'ppt' ? 'ppt_review' : 'review',
        progress: 100,
        content,
        pptProjectId,
        preview: pptProjectId ? `PPT项目已创建 · ${JSON.parse(content||'{}').slideCount || 0} 页` : content.replace(/<[^>]+>/g, ' ').replace(/\\s+/g, ' ').slice(0, 120) || `${item.name}已生成`,
        references: res.data?.references,
        generatedAt: res.data?.generatedAt,
        fileSize: res.data?.fileSize,
        step: GENERATION_STEPS[5],
        steps: GENERATION_STEPS,
      } : artifact));
      const finalName = res.data?.name || item.name;
      if (item.format === 'ppt') {
        // PPT 此时只完成了大纲和提示词，图片还在逐页生成，不能说"生成好了"
        messageApi.success(`${finalName} 大纲已生成，正在逐页生成PPT图片，请在预览中查看进度`);
        setMascotAnnouncement({ id: `gen-${item.id}-${Date.now()}`, text: `${finalName}的PPT大纲好啦，图片还在逐页生成，好了我再告诉你～` });
      } else {
        const noun = item.format === 'image' ? '图片' : '文档';
        messageApi.success(`${finalName} ${adjustMode ? '已按补充说明调整完成' : '已生成完成'}，可以预览或下载`);
        setMascotAnnouncement({ id: `gen-${item.id}-${Date.now()}`, text: adjustMode ? `${finalName}已经按你的意见调整好啦～` : `${finalName}这份${noun}生成好啦～` });
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || '生成失败';
      setArtifacts(prev => prev.map(artifact => artifact.id === item.id ? { ...artifact, status: 'failed', progress: 100, error: msg, step: `生成失败：${msg}` } : artifact));
      messageApi.error(`${item.name} 生成失败：${msg}`);
    } finally {
      window.clearInterval(timer);
    }
  };

  const runArtifactQueue = async (items: ArtifactItem[]) => {
    const queue = [...items];
    const workers = Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (queue.length) {
        const next = queue.shift();
        if (next) await generateSingleArtifact(next);
      }
    });
    await Promise.all(workers);
    // 批量生成的 loading 提示（duration: 0）此前从不关闭，这里在队列结束时统一收口
    messageApi.success({ content: '本批产物生成已全部结束，请到“成果与确认”查看', key: 'generate', duration: 2.5 });
    setTasks(prev => prev.map(task => task.status === 'running' ? { ...task, status: 'done', progress: 100 } : task));
    setActiveTab('outputs');
  };

  const updateMeetingRequirementSupplement = async (id: string, supplement: string) => {
    const next = meetingArtifactRequirements.map(item => item.id === id ? { ...item, userSupplement: supplement } : item);
    setMeetingArtifactRequirements(next);
    setArtifacts(prev => prev.map(item => item.meetingRequirement?.id === id ? { ...item, meetingRequirement: { ...item.meetingRequirement, userSupplement: supplement } } : item));
    if (currentTaskIdRef.current) {
      try {
        await api.put(`/ai-demand-center/tasks/${currentTaskIdRef.current}/meeting-artifact-requirements`, { requirements: next });
      } catch (error: any) {
        messageApi.error(error?.response?.data?.message || '会议需求产物补充保存失败');
      }
    }
  };

  const ignoreMeetingRequirement = async (id: string) => {
    const next = meetingArtifactRequirements.map(item => item.id === id ? { ...item, status: 'ignored' as const } : item);
    setMeetingArtifactRequirements(next);
    setSelectedArtifactIds(prev => prev.filter(itemId => itemId !== meetingRequirementArtifactId(id)));
    setArtifacts(prev => prev.filter(item => item.meetingRequirement?.id !== id));
    if (currentTaskIdRef.current) {
      await api.put(`/ai-demand-center/tasks/${currentTaskIdRef.current}/meeting-artifact-requirements`, { requirements: next }).catch(() => undefined);
    }
  };

  const handleStartGenerate = () => {
    if (!taskName.trim()) { messageApi.error('当前共创任务没有任务名称，请先在会议纪要页填写任务名称'); setActiveTab('analysis'); return; }
    if (!currentTaskIdRef.current) { messageApi.error('当前共创任务未保存，请先录音或生成会议纪要创建任务'); setActiveTab('meeting'); return; }
    if (!analysis) { messageApi.warning('请先生成会议纪要，再输出产物'); setActiveTab('analysis'); return; }
    if (!selectedModelId) { messageApi.warning('请先选择生成产物使用的大模型'); return; }
    const targets = runnableArtifacts.filter(item => selectedArtifactIds.includes(item.id));
    if (!targets.length) { messageApi.warning('请至少勾选一个要生成的产物文件'); return; }
    const start = () => {
      setActiveTab('templates');
      setArtifacts(prev => prev.map(item => targets.some(t => t.id === item.id) ? { ...item, status: 'queued', progress: 0, step: GENERATION_STEPS[0], steps: [GENERATION_STEPS[0]] } : item));
      setTasks(prev => prev.map((task, idx) => idx < Math.min(3, targets.length) ? { ...task, status: 'running', progress: 18, job: `并发生成队列 ${idx + 1}` } : task));
      messageApi.loading({ content: `已创建 ${targets.length} 个产物任务，正在后台生成`, key: 'generate', duration: 0 });
      void runArtifactQueue(targets);
    };
    const userUploadedCount = selectedMaterials.filter(item => item.id !== 'm1').length;
    if (userUploadedCount === 0) {
      Modal.confirm({
        title: '当前没有上传任何预备材料',
        content: '系统将仅根据会议纪要生成产物。你也可以取消后到“共创资料”上传文件，再回来生成。',
        okText: '仅根据会议纪要生成',
        cancelText: '去上传资料',
        onOk: start,
        onCancel: () => setActiveTab('materials'),
      });
      return;
    }
    start();
  };

  const handleRegenerateArtifact = (item: ArtifactItem) => {
    if (runningCount >= 3) { messageApi.warning('当前已有 3 个并发任务，请稍后再重新生成'); return; }
    void generateSingleArtifact(item, false);
  };

  /** 等 React 渲染出进度卡片后，把视图滚动到“生成进度”中对应产物的位置 */
  const scrollToProgressCard = (artifactId: string) => {
    window.setTimeout(() => {
      document.getElementById(`adc-progress-card-${artifactId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 160);
  };

  /** 会议需求产物卡片“生成该产物”：带即时反馈 + 自动跳转到下方生成进度（问题10修复） */
  const handleGenerateMeetingArtifact = (req: MeetingArtifactRequirement) => {
    const itemId = meetingRequirementArtifactId(req.id);
    const item = artifacts.find(artifact => artifact.id === itemId) || buildMeetingArtifactItem(req);
    if (!analysis) { messageApi.warning('请先生成会议纪要，再生成该产物'); setActiveTab('analysis'); return; }
    if (!selectedModelId) { messageApi.warning('请先在“会议录音”页选择分析模型，再生成该产物'); return; }
    if (item.status === 'running' || item.status === 'queued') {
      messageApi.info(`「${item.name}」正在生成中，已为你定位到下方生成进度`);
      scrollToProgressCard(itemId);
      return;
    }
    if (runningCount >= 3) { messageApi.warning('当前已有 3 个并发生成任务，请稍后再生成'); return; }
    messageApi.success(`已开始生成「${item.name}」，可在下方“生成进度”查看实时状态`);
    void generateSingleArtifact(item, false);
    scrollToProgressCard(itemId);
  };

  const handleAdjustArtifact = (item: ArtifactItem) => {
    if (!item.content) { messageApi.warning('这个产物还没有上次结果，不能基于内容调整，请先生成一次'); return; }
    if (!(artifactNotes[item.id] || '').trim()) { messageApi.warning('请先在补充说明中写清楚要调整哪些内容'); return; }
    if (runningCount >= 3) { messageApi.warning('当前已有 3 个并发任务，请稍后再调整'); return; }
    void generateSingleArtifact(item, true);
  };

  const handleScopeChange = (value: OutputScope) => {
    setScope(value);
    setTasks(prev => prev.map(item => item.id === 'a5' ? { ...item, job: value === 'pre_plus_rd' ? '研发产物已纳入并发队列' : '研发产物未勾选，暂不执行' } : item));
  };

  const handleConfirmArtifact = async (id: string) => {
    await persistArtifactStatus(id, 'confirmed', '已确认', '已确认，刷新页面后仍会保留这个版本');
  };

  const persistArtifactStatus = async (id: string, status: ArtifactStatus, step: string, successMessage?: string) => {
    let nextArtifacts: ArtifactItem[] = [];
    setArtifacts(prev => {
      nextArtifacts = prev.map(item => item.id === id ? { ...item, status, step } : item);
      return nextArtifacts;
    });
    try {
      if (currentTaskIdRef.current) {
        const persist = nextArtifacts.filter(item => item.id !== 'minutes' && item.content).map(item => ({
          id: item.id,
          name: item.name,
          format: item.format,
          content: item.content,
          status: item.status,
          generatedAt: item.generatedAt,
          fileSize: item.fileSize || (item.content ? new Blob([item.content]).size : 0),
          references: item.references,
          pptProjectId: item.pptProjectId,
          meetingRequirement: item.meetingRequirement,
        }));
        await api.put(`/ai-demand-center/tasks/${currentTaskIdRef.current}`, { artifacts: persist });
      }
      if (successMessage) messageApi.success(successMessage);
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || '产物状态保存失败');
    }
  };

  const tencentMeetingDetail = useMemo(() => {
    const meeting = tencentMeetingInfo?.meeting || {};
    const meetingId = String(meeting.meeting_id || tencentMeetingInfo?.meetingId || tencentSelectedMeetingId || '').trim();
    const meetingCode = String(meeting.meeting_code || '').trim();
    const joinUrl = String(meeting.join_url || (meetingCode ? `https://meeting.tencent.com/dm/${meetingCode}` : '')).trim();
    return {
      meeting,
      meetingId,
      meetingCode,
      joinUrl,
      recordId: String(tencentMeetingInfo?.recordId || extractTencentRecordId(tencentMeetingInfo) || tencentRecordId || '').trim(),
      recordCount: Array.isArray(tencentMeetingInfo?.records) ? tencentMeetingInfo.records.length : 0,
      subject: String(meeting.subject || '-'),
      status: String(meeting.status || '-'),
      type: String(meeting.meeting_type || '-'),
      start: formatTencentTime(meeting.start_time),
      end: formatTencentTime(meeting.end_time),
      duration: getDurationText(meeting.start_time, meeting.end_time),
      hostText: meeting.hosts?.map((h:any) => {
        const nickname = String(h.user_name || h.userName || '').trim();
        const account = String(h.userid || h.operator_id || h.open_id || '').trim();
        if (nickname && account && nickname !== account) return `${nickname}（${account}）`;
        return nickname || account;
      }).filter(Boolean).join(', ') || '-',
      participantCount: Number.isFinite(Number(tencentMeetingInfo?.total_participants)) ? String(tencentMeetingInfo.total_participants) : '-',
    };
  }, [tencentMeetingInfo, tencentSelectedMeetingId]);

  return (
    <div className="adc-demo-page">
      {contextHolder}
      <LightFlowCanvas />
      <LiveMeetingCopilotMascot
        activeTab={activeTab}
        recordStatus={recordStatus}
        hasTranscript={!!fullTranscript}
        analysisLoading={analysisLoading}
        runningCount={runningCount}
        useKnowledgeBase={useKnowledgeBase}
        liveInsight={liveInsight}
        visible={activeTab === 'meeting' || recordStatus !== 'idle' || !!liveInsight || !!fullTranscript}
        analyzing={liveInsightLoading}
        interjection={mascotInterjection}
        onInterjectionRespond={respondMascotInterjection}
        announcement={mascotAnnouncement}
        meetingTaskId={currentTaskId}
        onManualInsight={() => requestLiveInsight(transcriptRef.current, partialTextRef.current, true)}
        onOpenMeetingTab={() => {
          setActiveTab('meeting');
          setHighlightRecordingBtn(true);
          setTimeout(() => setHighlightRecordingBtn(false), 3000);
          // 已在会议录音 tab 时直接滚动到录音区域；切 tab 时需要等 DOM 更新后再滚动
          const scrollToMeeting = () => {
            const el = document.querySelector('.adc-meeting-grid');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          };
          if (activeTab === 'meeting') {
            scrollToMeeting();
          } else {
            requestAnimationFrame(() => requestAnimationFrame(scrollToMeeting));
          }
        }}
      />
      <header className="adc-demo-topbar">
        <div className="adc-demo-brand"><span className="adc-demo-brand-icon"><ThunderboltOutlined /></span><div><h1>AI需求共创</h1><p>直接从录音开始，无需先创建项目</p></div></div>
        <div className="adc-demo-actions"><Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>历史共创</Button><Button icon={<SettingOutlined />} onClick={() => setTemplateOpen(true)}>模板管理</Button></div>
      </header>
      <main className="adc-demo-main adc-demo-main-tabs">

        {/* 当前共创任务条 */}
        <section className="adc-demo-project-strip">
          <div className="adc-demo-project-left">
            <span className="adc-demo-mini-icon"><ProjectOutlined /></span>
            <div>
              <Text type="secondary">当前共创任务</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Select
                  value={currentTaskId || '__new__'}
                  onChange={val => val === '__new__' ? handleNewTask() : loadTask(val)}
                  className="adc-demo-project-select"
                  popupMatchSelectWidth={420}
                  loading={taskLoading}
                  disabled={taskLoading}
                >
                  <Select.Option value="__new__">
                    <span style={{ color: '#0891b2', fontWeight: 700 }}>+ 新建任务（直接录音）</span>
                  </Select.Option>
                  {demandTasks.map(task => (
                    <Select.Option key={task.id} value={task.id}>
                      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{task.name}</span>
                        <Tag color={task.status === 'analyzed' ? 'green' : task.status === 'recorded' ? 'blue' : 'default'} style={{ marginLeft: 8 }}>
                          {task.status === 'draft' ? '草稿' : task.status === 'recorded' ? '已录音' : task.status === 'analyzed' ? '已分析' : task.status}
                        </Tag>
                      </span>
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <div className="adc-demo-project-meta">
            {isNewTask ? (
              <Tag color="cyan">新建任务</Tag>
            ) : (
              <>
                <Tag color={analysis ? 'green' : 'blue'}>{analysis ? '已分析' : '已录音'}</Tag>
                <Text type="secondary">{fullTranscript.length} 字</Text>
              </>
            )}
          </div>
        </section>

        <Tabs className="adc-tabs" activeKey={activeTab} onChange={setActiveTab} items={[
          { key: 'meeting', label: '会议录音', children: <section className="adc-meeting-grid">
            <aside className="adc-intro-panel">
              <div className="adc-intro-badge">
                <span className="adc-intro-dot" />
                <span>三步完成需求共创</span>
              </div>
              <h2>从录音开始<br />AI 帮你沉淀一切</h2>
              <p>不需要先创建项目。可实时录音，也可导入原始录音转写或 Word 文档，直接进入会议纪要创建。</p>
              <div className="adc-intro-list">
                <div className="adc-intro-step">
                  <span className="adc-intro-step-num">1</span>
                  <div><strong>录音/导入</strong><span>支持实时录音，也可导入 doc、docx、txt 原始记录</span></div>
                </div>
                <div className="adc-intro-step">
                  <span className="adc-intro-step-num">2</span>
                  <div><strong>AI 提炼</strong><span>4 种会议纪要模式可选，输出精美 HTML</span></div>
                </div>
                <div className="adc-intro-step">
                  <span className="adc-intro-step-num">3</span>
                  <div><strong>生成产物</strong><span>一键生成总体思路、PRD、项目计划等</span></div>
                </div>
              </div>
              <Alert type="info" showIcon message="语音转文字由后台配置，页面只选择分析模型。录音需要 HTTPS 安全连接。" style={{borderRadius:10,background:'#f0f9ff',border:'1px solid #bae6fd'}} />
            </aside>
            <section className={`adc-recorder-panel ${recordStatus === 'recording' ? 'adc-recording-active' : ''} ${recordStatus === 'error' ? 'adc-recording-error' : ''}`}>
              <div className="adc-recorder-head">
                <div className="adc-recorder-status">
                  {recordStatus === 'recording' ? (
                    <>
                      <span className="adc-demo-rec-dot active big" />
                      <strong>正在录音</strong>
                      <span className="adc-rec-timer">{fullTranscript.length > 0 ? `${fullTranscript.length} 字` : ''}</span>
                    </>
                  ) : recordStatus === 'connecting' ? (
                    <><LoadingOutlined spin /><strong>正在连接转写服务</strong></>
                  ) : recordStatus === 'stopping' ? (
                    <><LoadingOutlined spin /><strong>正在保存</strong></>
                  ) : recordStatus === 'error' ? (
                    <><span className="adc-demo-rec-dot error" /><strong>连接失败</strong></>
                  ) : (
                    <>
                      <div className="adc-recorder-ready-icon">
                        <PlayCircleOutlined style={{fontSize:16}} />
                      </div>
                      <div>
                        <strong>准备录音</strong>
                        <span style={{display:'block',fontSize:12,color:'#64748b'}}>选择分析模型后点击下方按钮开始</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className={`adc-demo-wave ${recordStatus === 'recording' ? 'active' : ''}`}><i /><i /><i /><i /><i /><i /><i /><i /><i /></div>

              <div className="adc-record-inline-controls">
                <span className="adc-model-label">分析模型</span>
                <Select className="adc-clean-select adc-model-select-inline" showSearch allowClear value={selectedModelId} onChange={setSelectedModelId} placeholder="选择分析模型" options={modelOptions} />
                <div className="adc-recorder-actions compact">
                  {recordStatus === 'idle' || recordStatus === 'error' ? (
                    <Button type="primary" size="large" icon={<PlayCircleOutlined />} onClick={startRecording} className={highlightRecordingBtn ? 'adc-rec-btn-highlight' : ''}>
                      {fullTranscript ? '继续录音' : '开始录音'}
                    </Button>
                  ) : recordStatus === 'connecting' ? (
                    <Button type="primary" size="large" icon={<LoadingOutlined />} disabled>连接中</Button>
                  ) : recordStatus === 'recording' ? (
                    <Button danger size="large" icon={<PauseCircleOutlined />} onClick={() => stopRecording(false)}>
                      暂停保存
                    </Button>
                  ) : recordStatus === 'stopping' ? (
                    <Button size="large" icon={<LoadingOutlined />} disabled>保存中</Button>
                  ) : null}
                </div>
              </div>

              <div className="adc-import-strip">
                <div className="adc-import-strip-main">
                  <span className="adc-import-strip-icon"><InboxOutlined /></span>
                  <div>
                    <strong>导入原始录音文件 / Word 文档</strong>
                    <span>支持 .doc、.docx、.txt，解析后自动写入当前共创任务，下一步直接生成会议纪要。</span>
                  </div>
                </div>
                <Upload
                  accept=".doc,.docx,.txt"
                  showUploadList={false}
                  beforeUpload={file => { void handleMeetingFileImport(file as File); return false; }}
                  disabled={meetingFileImporting || recordStatus === 'recording' || recordStatus === 'connecting' || recordStatus === 'stopping'}
                >
                  <Button className="adc-import-btn" size="large" icon={meetingFileImporting ? <LoadingOutlined /> : <CloudUploadOutlined />} loading={meetingFileImporting}>
                    导入文件
                  </Button>
                </Upload>
              </div>

              {fullTranscript && recordStatus !== 'recording' ? (
                <>
                  <div className="adc-mode-cards">
                    {ANALYSIS_MODES.map(mode => (
                      <button
                        key={mode.key}
                        type="button"
                        className={`adc-mode-card${analysisMode === mode.key ? ' selected' : ''}`}
                        onClick={() => setAnalysisMode(mode.key)}
                      >
                        <span className="adc-mode-card-icon">{mode.icon}</span>
                        <div className="adc-mode-card-body">
                          <strong>{mode.name}</strong>
                          <span>{mode.desc}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="adc-meeting-next-step">
                    <div>
                      <strong>会议全文已准备好，选择会议纪要模式后即可分析</strong>
                      <span>{selectedModelId ? '点击按钮，AI 会基于录音/导入文本生成专业 HTML 会议纪要。' : '请先选择分析模型，然后点击按钮创建会议纪要。'}</span>
                    </div>
                    <Button
                      type="primary"
                      size="large"
                      icon={analysisLoading ? <LoadingOutlined /> : <RocketOutlined />}
                      loading={analysisLoading}
                      disabled={!selectedModelId}
                      onClick={() => analyzeTranscript()}
                    >
                      生成会议纪要
                    </Button>
                  </div>
                </>
              ) : null}

              <div className="adc-live-transcript">
                <div><strong>实时转写（当前最新一句）</strong><span>{partialText ? `${partialText.length} 字` : ''}</span></div>
                {partialText ? (
                  <pre>{partialText}</pre>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={recordStatus === 'recording' ? '正在聆听当前这句话...' : '开始录音后这里显示当前最新识别'} />
                )}
              </div>

              <div className="adc-full-transcript">
                <div className="adc-full-transcript-head">
                  <strong>会议全文内容</strong>
                  <span>{fullTranscript.length > 0 ? `${fullTranscript.length} 字 · 已自动保存到当前共创任务` : '录音或导入后会显示在这里，并自动保存到当前共创任务'}</span>
                </div>
                {fullTranscript ? (
                  <pre>{fullTranscript}</pre>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="可开始录音，也可导入 doc/docx/txt 原始记录，内容会在这里汇总" />
                )}
              </div>
            </section>
            <section className="adc-tmeet-panel">
              <div className="adc-tmeet-orb"><TeamOutlined /></div>
              <div className="adc-tmeet-main">
                <div className="adc-tmeet-head">
                  <div>
                    <span className="adc-tmeet-kicker">Tencent Meeting Link</span>
                    <h3>腾讯会议联动</h3>
                    <p>创建带云录制/转写的腾讯会议；会后点击提取，把录音转写或腾讯会议智能纪要同步到当前共创任务。</p>
                  </div>
                  <div className="adc-tmeet-status">
                    <Space size={8}>
                      <Tag color={tencentMeetingStatus?.authorized ? 'green' : 'gold'}>{tencentMeetingStatus?.authorized ? 'CLI已授权' : '待授权/待检查'}</Tag>
                      <Button
                        size="small"
                        className="adc-tmeet-auth-btn"
                        type={tencentMeetingStatus?.authorized ? 'default' : 'primary'}
                        icon={<SafetyCertificateOutlined />}
                        loading={tencentStatusLoading}
                        onClick={openTmeetAuth}
                      >
                        {tencentMeetingStatus?.authorized ? '重新授权' : '授权登录'}
                      </Button>
                    </Space>
                    {tencentMeetingStatus?.message && <span className="adc-tmeet-status-msg">{tencentMeetingStatus.message}</span>}
                  </div>
                </div>
                <div className="adc-tmeet-body adc-tmeet-redesign-v67">
                  <div className="adc-tmeet-create-card adc-tmeet-card">
                    <div className="adc-tmeet-card-head">
                      <div>
                        <span className="adc-tmeet-step">01</span>
                        <strong>创建腾讯会议</strong>
                      </div>
                      <span>自动开启云录制和转写</span>
                    </div>
                    <div className="adc-tmeet-create-form">
                      <label className="adc-tmeet-field adc-tmeet-field-subject">
                        <span>会议主题</span>
                        <Input value={tencentSubject} onChange={e => setTencentSubject(e.target.value)} placeholder="请输入会议主题" />
                      </label>
                      <label className="adc-tmeet-field adc-tmeet-field-start">
                        <span>开始时间</span>
                        <input className="adc-tmeet-datetime" type="datetime-local" value={tencentStart} onChange={e => setTencentStart(e.target.value)} />
                      </label>
                      <label className="adc-tmeet-field adc-tmeet-field-duration">
                        <span>时长</span>
                        <Select
                          className="adc-tmeet-duration-select"
                          value={tencentDuration}
                          onChange={setTencentDuration}
                          options={[
                            { value: 15, label: '15 分钟' },
                            { value: 30, label: '30 分钟' },
                            { value: 60, label: '1 小时' },
                            { value: 120, label: '2 小时' },
                            { value: 180, label: '3 小时' },
                          ]}
                        />
                      </label>
                      <div className="adc-tmeet-create-actions">
                        <Button type="primary" className="adc-clean-primary adc-tmeet-create-btn" icon={tencentCreateLoading ? <LoadingOutlined /> : <PlusOutlined />} loading={tencentCreateLoading} onClick={createTencentMeeting}>创建会议</Button>
                        <Button className="adc-quick-meeting-btn" icon={tencentCreateLoading ? <LoadingOutlined /> : <ThunderboltOutlined />} loading={tencentCreateLoading} onClick={createQuickMeeting}>快速会议</Button>
                      </div>
                    </div>
                  </div>

                  <div className="adc-tmeet-extract-card adc-tmeet-card">
                    <div className="adc-tmeet-card-head">
                      <div>
                        <span className="adc-tmeet-step">02</span>
                        <strong>会后提取</strong>
                      </div>
                      <Space size={8} wrap>
                        <Button className="adc-tmeet-load-btn" icon={<ReloadOutlined />} loading={tencentMeetingLoading} onClick={() => fetchTencentCurrentMeetings(true)}>刷新当前/待开始</Button>
                        <Button className="adc-tmeet-load-btn" icon={<ReloadOutlined />} loading={tencentMeetingLoading} onClick={fetchTencentMeetings}>加载已结束会议</Button>
                        <Button className="adc-tmeet-load-btn" disabled={tencentMeetingInfoLoading || !tencentSelectedMeetingId.trim()} loading={tencentMeetingInfoLoading} onClick={fetchTencentMeetingInfo}>刷新所选会议详情</Button>
                      </Space>
                    </div>
                    <div className="adc-tmeet-extract-form">
                      <label className="adc-tmeet-field adc-tmeet-field-meeting">
                        <span>当前/待开始 / 已结束会议</span>
                        <Select
                          className="adc-tmeet-select"
                          allowClear
                          showSearch
                          placeholder="刷新页面后自动加载当前/待开始会议"
                          value={selectableTencentMeetings.some(item => getTencentMeetingId(item) === tencentSelectedMeetingId) ? tencentSelectedMeetingId : undefined}
                          onChange={val => { const v = val || ''; setTencentSelectedMeetingId(v); if (v) fetchTencentMeetingInfoById(v); else { setTencentMeetingInfo(null); setTencentRecordId(''); } }}
                          options={selectableTencentMeetings.map(item => ({ value: getTencentMeetingId(item), label: getTencentMeetingLabel(item) }))}
                          notFoundContent={tencentMeetingLoading ? '正在加载会议...' : '暂无当前/待开始会议；也可加载已结束会议'}
                        />
                        <Input.Search
                          className="adc-tmeet-meeting-input"
                          value={tencentSelectedMeetingId}
                          onChange={e => setTencentSelectedMeetingId(e.target.value)}
                          onSearch={fetchTencentMeetingInfo}
                          enterButton="查询"
                          placeholder="特殊情况可手动输入会议ID/会议号"
                          loading={tencentMeetingInfoLoading}
                        />
                      </label>
                      <label className="adc-tmeet-field adc-tmeet-field-record">
                        <span>录制 ID</span>
                        <Input value={tencentRecordId} onChange={e => setTencentRecordId(e.target.value)} placeholder="可直接填写 recordId" />
                      </label>
                      <label className="adc-tmeet-field adc-tmeet-field-mode">
                        <span>提取内容</span>
                        <Select className="adc-tmeet-mode-select" value={tencentExtractMode} onChange={setTencentExtractMode} options={[{ value: 'recording', label: '录音转写' }, { value: 'minutes', label: '会议纪要' }]} />
                      </label>
                      <Button type="primary" className="adc-tmeet-fetch-btn" icon={tencentExtractLoading ? <LoadingOutlined /> : <CloudUploadOutlined />} loading={tencentExtractLoading} onClick={extractTencentMeeting}>提取到共创</Button>
                    </div>
                  </div>

                  <div className="adc-tmeet-info adc-tmeet-card">
                    <div className="adc-tmeet-info-head">
                      <div>
                        <span className="adc-tmeet-step">03</span>
                        <strong>会议详情</strong>
                      </div>
                      <Button className="adc-tmeet-refresh-btn" icon={<ReloadOutlined />} loading={tencentMeetingInfoLoading} disabled={!tencentSelectedMeetingId.trim()} onClick={fetchTencentMeetingInfo} size="small" type="text">刷新</Button>
                    </div>
                    {tencentMeetingInfoLoading ? (
                      <div className="adc-tmeet-info-loading"><LoadingOutlined spin style={{ fontSize: 24 }} /><span>正在读取会议详情</span></div>
                    ) : tencentMeetingInfo ? (
                      <div className="adc-tmeet-info-content">
                        <div className="adc-tmeet-info-hero">
                          <span>会议号</span>
                          <strong>{tencentMeetingDetail.meetingCode || '-'}</strong>
                          <CopyOutlined className="adc-tmeet-copy-icon" onClick={() => copyTencentText(tencentMeetingDetail.meetingCode, '会议号')} />
                        </div>
                        <div className="adc-tmeet-info-body">
                          <div className="adc-tmeet-info-row"><span className="adc-tmeet-info-label">会议ID</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.meetingId || '-'}</span><CopyOutlined className="adc-tmeet-copy-icon" onClick={() => copyTencentText(tencentMeetingDetail.meetingId, '会议ID')} /></div>
                          <div className="adc-tmeet-info-row"><span className="adc-tmeet-info-label">录制ID</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.recordId || '-'}</span>{tencentMeetingDetail.recordId && <CopyOutlined className="adc-tmeet-copy-icon" onClick={() => copyTencentText(tencentMeetingDetail.recordId, '录制ID')} />}</div>
                          <div className="adc-tmeet-info-row"><span className="adc-tmeet-info-label">录制文件</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.recordCount ? `${tencentMeetingDetail.recordCount} 个` : '未查询到'}</span></div>
                          <div className="adc-tmeet-info-row adc-tmeet-info-row-wide"><span className="adc-tmeet-info-label">主题</span><span className="adc-tmeet-info-value adc-tmeet-info-value-subject">{tencentMeetingDetail.subject}</span><CopyOutlined className="adc-tmeet-copy-icon" onClick={() => copyTencentText(tencentMeetingDetail.subject === '-' ? '' : tencentMeetingDetail.subject, '会议主题')} /></div>
                          <div className="adc-tmeet-info-row adc-tmeet-status-row"><span className="adc-tmeet-info-label">状态</span><span className={`adc-tmeet-status-badge ${tencentMeetingDetail.status.includes('中') ? 'is-live' : tencentMeetingDetail.status.includes('待') ? 'is-upcoming' : ''}`}>{tencentMeetingDetail.status}</span></div>
                          <div className="adc-tmeet-info-row"><span className="adc-tmeet-info-label">类型</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.type}</span></div>
                          <div className="adc-tmeet-info-row"><span className="adc-tmeet-info-label">开始时间</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.start}</span><CopyOutlined className="adc-tmeet-copy-icon" onClick={() => copyTencentText(tencentMeetingDetail.start === '-' ? '' : tencentMeetingDetail.start, '开始时间')} /></div>
                          <div className="adc-tmeet-info-row"><span className="adc-tmeet-info-label">持续时间</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.duration}</span><CopyOutlined className="adc-tmeet-copy-icon" onClick={() => copyTencentText(tencentMeetingDetail.duration === '-' ? '' : tencentMeetingDetail.duration, '持续时间')} /></div>
                          <div className="adc-tmeet-info-row"><span className="adc-tmeet-info-label">结束时间</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.end}</span><CopyOutlined className="adc-tmeet-copy-icon" onClick={() => copyTencentText(tencentMeetingDetail.end === '-' ? '' : tencentMeetingDetail.end, '结束时间')} /></div>
                          <div className="adc-tmeet-info-row adc-tmeet-info-row-strong"><span className="adc-tmeet-info-label">主持人</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.hostText}</span></div>
                          <div className="adc-tmeet-info-row adc-tmeet-info-row-strong adc-tmeet-info-row-count"><span className="adc-tmeet-info-label">参会人数</span><span className="adc-tmeet-info-value">{tencentMeetingDetail.participantCount}<em>人在线</em></span></div>
                          {tencentMeetingDetail.joinUrl && (
                            <div className="adc-tmeet-info-row adc-tmeet-info-link-row">
                              <span className="adc-tmeet-info-label">会议链接</span>
                              <div className="adc-tmeet-link-wrap">
                                <a className="adc-tmeet-link" href={tencentMeetingDetail.joinUrl} target="_blank" rel="noopener noreferrer">{tencentMeetingDetail.joinUrl}</a>
                                <CopyOutlined className="adc-tmeet-copy-icon" onClick={() => copyTencentText(tencentMeetingDetail.joinUrl, '会议链接')} />
                              </div>
                            </div>
                          )}
                        </div>
                        {tencentMeetingInfo.participants?.length > 0 && (
                          <div className="adc-tmeet-info-participants">
                            <strong>参会人</strong>
                            {tencentMeetingInfo.participants.map((p: any, i: number) => (
                              <div key={i} className="adc-tmeet-participant-row"><span>{p.user_name || p.userid || p.open_id || p.uuid}</span><Tag color={p.is_host ? 'cyan' : p.status === '已入会' ? 'green' : 'default'}>{p.is_host ? (p.user_role || '主持人') : p.status}</Tag></div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="adc-tmeet-info-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="选择或创建会议后，这里展示会议号、时间和参会信息" /></div>
                    )}
                  </div>

                  <div className="adc-tmeet-foot">
                    <span>刷新页面会自动展示最近一个当前/待开始会议；没有待开始会议时，自动展示最近一次历史会议详情。</span>
                    <Button type="link" size="small" loading={tencentStatusLoading} onClick={fetchTencentMeetingStatus}>重新检查CLI</Button>
                  </div>
                </div>
              </div>
            </section>
          </section> },
          { key: 'materials', label: '共创资料', children: <section className="adc-demo-panel adc-tab-panel adc-material-workspace">
            <div className="adc-demo-panel-title"><div><span>01</span><h3>共创资料</h3><p>产物生成前会优先引用这里勾选的资料；没有上传资料时会二次确认。</p></div><Button className="adc-clean-primary adc-upload-main-btn" type="primary" icon={<CloudUploadOutlined />} onClick={() => setUploadOpen(true)}>上传资料</Button></div>
            <div className="adc-demo-folder-card adc-folder-premium"><FolderOpenOutlined /><div className="adc-folder-copy"><strong>AI需求共创资料夹</strong><p>与当前共创任务绑定；转写和HTML会议纪要会自动作为首要资料。</p></div><Tag className="adc-folder-count" color="cyan">{selectedMaterials.length}/{materials.length} 已引用</Tag></div>
            <div className="adc-material-list">{materials.map(item => <label key={item.id} className="adc-material-row"><Checkbox checked={!!item.selected} onChange={e => setMaterials(prev => prev.map(m => m.id === item.id ? { ...m, selected: e.target.checked } : m))} /><div><strong>{item.name}</strong><p>{item.kind} · {item.meta}</p></div><Tag color={item.status === '已解析' ? 'green' : 'gold'}>{item.status}</Tag></label>)}</div>
          </section> },
          { key: 'analysis', label: '会议纪要', children: <section className="adc-demo-panel adc-tab-panel">
            {renderIntelligencePanel()}
            {analysisError && (
              <Alert
                style={{ marginBottom: 16 }}
                type="error"
                showIcon
                message="AI生成会议纪要失败"
                description={analysisError}
              />
            )}
            {!analysis ? (
              <Empty description="结束录音并选择模型后，会议纪要会显示在这里" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <>
                <div className="adc-naming">
                  <div className="adc-naming-body">
                    <strong>任务名称</strong>
                    <span>AI 根据会议内容建议的名称，你可以修改后确认</span>
                  </div>
                  <div className="adc-naming-input">
                    <Input
                      value={taskName}
                      onChange={e => {
                        setTaskName(e.target.value);
                        if (currentTaskId) api.put(`/ai-demand-center/tasks/${currentTaskId}`, { name: e.target.value }).catch(() => {});
                      }}
                      onBlur={() => fetchTasks()}
                      placeholder="输入任务名称"
                      style={{ borderRadius: 8 }}
                      suffix={<Button className="adc-clean-primary" type="primary" size="small" icon={<RocketOutlined />} onClick={() => { setActiveTab('templates'); }}>生成产物</Button>}
                    />
                  </div>
                </div>
                <div className="adc-analysis-hero">
                  <div className="adc-analysis-hero-main">
                    <span className="adc-analysis-step">03</span>
                    <div>
                      <p className="adc-analysis-kicker">AI 分析结果</p>
                      <h3>会议纪要已整理成结构化文档</h3>
                      <div className="adc-analysis-meta">
                        <Tag color="cyan">{ANALYSIS_MODES.find(m => m.key === analysisMode)?.name || '通用标准版'}</Tag>
                        <span>{taskName || 'AI需求共创任务'}</span>
                      </div>
                    </div>
                  </div>
                  <Button className="adc-clean-default" icon={<ReloadOutlined />} loading={analysisLoading} onClick={() => analyzeTranscript()}>重新生成</Button>
                </div>
                <div className="adc-minutes-export-bar">
                  <div className="adc-minutes-export-copy">
                    <span className="adc-minutes-export-icon"><DownloadOutlined /></span>
                    <div>
                      <strong>导出原始会议纪要</strong>
                      <span>正式文档排版，保留标题层级、清单、表格与完整会议内容</span>
                    </div>
                  </div>
                  <Space className="adc-minutes-export-actions" wrap>
                    <Button
                      className="adc-clean-default adc-minutes-word-btn"
                      icon={<FileWordOutlined />}
                      loading={minutesExporting === 'docx'}
                      disabled={minutesExporting !== null && minutesExporting !== 'docx'}
                      onClick={() => exportMeetingMinutes('docx')}
                    >
                      导出 Word
                    </Button>
                    <Button
                      className="adc-clean-primary adc-minutes-pdf-btn"
                      type="primary"
                      icon={<FilePdfOutlined />}
                      loading={minutesExporting === 'pdf'}
                      disabled={minutesExporting !== null && minutesExporting !== 'pdf'}
                      onClick={() => exportMeetingMinutes('pdf')}
                    >
                      导出 PDF
                    </Button>
                  </Space>
                </div>
                <MinutesHtmlView html={formatMinutesHtml(analysis, taskName, ANALYSIS_MODES.find(m => m.key === analysisMode)?.name || '通用标准版')} />
                <div className="adc-analysis-next-card">
                  <div>
                    <strong>下一步：根据会议纪要生成产物文件</strong>
                    <span>进入共创产物后，选择产物类型，系统会引用会议纪要、共创资料和知识库生成正文。</span>
                  </div>
                  <Button className="adc-clean-primary" type="primary" size="large" icon={<RocketOutlined />} onClick={() => setActiveTab('templates')}>
                    前往生成产物
                  </Button>
                </div>
              </>
            )}
          </section> },
          { key: 'templates', label: '共创产物', children: <section className="adc-demo-panel adc-tab-panel adc-generate-workspace"><div className="adc-demo-panel-title adc-demo-panel-title-large"><div><span>02</span><h3>产物模板与输出配置</h3><p>选择模板后可额外搜索勾选产物；生成时最多 3 个并发，逐个完成逐个提示。</p></div><Button className="adc-clean-primary adc-start-generate-btn" type="primary" size="large" icon={<SendOutlined />} onClick={handleStartGenerate}>开始生成</Button></div>
            <div className="adc-generate-control"><div className="adc-demo-choice-card wide"><label>知识库匹配</label><div className="adc-kb-toggle"><Switch checked={useKnowledgeBase} onChange={setUseKnowledgeBase} /><span>{useKnowledgeBase ? '默认强匹配平台知识库' : '本次不匹配知识库'}</span></div></div><div className="adc-demo-choice-card wide"><label>生成范围</label><div className="adc-scope-segment"><button type="button" className={scope === 'pre_only' ? 'active' : ''} onClick={() => handleScopeChange('pre_only')}><strong>产前产物</strong><span>蓝图 / 需求 / PRD / 计划</span></button><button type="button" className={scope === 'pre_plus_rd' ? 'active' : ''} onClick={() => handleScopeChange('pre_plus_rd')}><strong>产前 + 研发</strong><span>额外包含技术方案和交接包</span></button></div></div></div>
            <div className="adc-demo-template-grid">{TEMPLATES.map(item => <button type="button" key={item.id} className={item.id === templateId ? 'adc-demo-template active' : 'adc-demo-template'} onClick={() => setTemplateId(item.id)}><div className="adc-demo-template-head"><BookOutlined />{item.id === templateId && <CheckCircleOutlined />}</div><h4>{item.name}</h4><p>{item.desc}</p><div>{item.chips.map(chip => <Tag key={chip}>{chip}</Tag>)}</div></button>)}</div>
            {meetingArtifactRequirements.filter(req => req.status !== 'ignored').length > 0 && <div className="adc-meeting-artifacts-panel"><div className="adc-meeting-artifacts-head"><div><span>Meeting Required Artifacts</span><h4>会议需求产物</h4><p>根据会议纪要自动识别出的待产出文件，生成时会强制匹配知识库并携带下方具体要求。</p></div><Tag color="cyan">{meetingArtifactRequirements.filter(req => req.status !== 'ignored').length} 个建议产物</Tag></div><div className="adc-meeting-artifact-grid">{meetingArtifactRequirements.filter(req => req.status !== 'ignored').map(req => { const itemId = meetingRequirementArtifactId(req.id); return <article key={req.id} className={`adc-meeting-artifact-card ${selectedArtifactIds.includes(itemId) ? 'selected' : ''}`}><div className="adc-meeting-artifact-card-top"><Checkbox checked={selectedArtifactIds.includes(itemId)} onChange={e => setSelectedArtifactIds(prev => e.target.checked ? Array.from(new Set([...prev, itemId])) : prev.filter(id => id !== itemId))} /><div><strong>{req.title}</strong><p>{req.description || req.reason}</p></div><Tag color={req.priority === 'high' ? 'red' : req.priority === 'low' ? 'default' : 'blue'}>{req.priority === 'high' ? '高优先级' : req.priority === 'low' ? '低优先级' : '中优先级'}</Tag></div><div className="adc-meeting-artifact-tags"><Tag>{req.sourceType === 'explicit' ? '会议明确要求' : 'AI建议补充'}</Tag><Tag>{artifactFormatLabel(req.outputFormat)}</Tag><Tag>{req.artifactType || 'custom'}</Tag></div>{req.requiredContents?.length ? <ul className="adc-meeting-artifact-list">{req.requiredContents.slice(0, 6).map(content => <li key={content}>{content}</li>)}</ul> : null}{req.evidence?.length ? <div className="adc-meeting-artifact-evidence"><span>会议依据</span><p>{req.evidence.slice(0, 2).join('；')}</p></div> : null}<TextArea className="adc-meeting-artifact-input" autoSize={{ minRows: 2, maxRows: 4 }} placeholder="补充这个产物的格式、章节、侧重点、禁写内容等要求" value={req.userSupplement || ''} onChange={e => { const value = e.target.value; setMeetingArtifactRequirements(prev => prev.map(item => item.id === req.id ? { ...item, userSupplement: value } : item)); setArtifacts(prev => prev.map(item => item.meetingRequirement?.id === req.id ? { ...item, meetingRequirement: { ...item.meetingRequirement, userSupplement: value } } : item)); }} onBlur={e => updateMeetingRequirementSupplement(req.id, e.target.value)} /><div className="adc-meeting-artifact-actions">{(() => { const current = artifacts.find(item => item.id === itemId); const generating = current?.status === 'running' || current?.status === 'queued'; return <Button size="small" type="primary" icon={generating ? <LoadingOutlined /> : <SendOutlined />} loading={generating} disabled={!analysis || generating} onClick={() => handleGenerateMeetingArtifact(req)}>{generating ? '生成中…' : current?.content ? '重新生成' : '生成该产物'}</Button>; })()}<Button size="small" type="text" danger onClick={() => ignoreMeetingRequirement(req.id)}>忽略</Button></div></article>; })}</div></div>}
            <div className={`adc-artifact-picker ${pickerExpanded ? '' : 'adc-artifact-picker-collapsed'}`}><div className="adc-artifact-picker-head"><div><strong>产物文件类型</strong><span className="adc-artifact-picker-toggle" onClick={() => setPickerExpanded(v => !v)}>{pickerExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}</span></div><Input.Search className="adc-clean-search" allowClear placeholder="搜索 PRD / 方案 / 汇报稿" value={artifactKeyword} onChange={e => setArtifactKeyword(e.target.value)} /></div><div className="adc-artifact-picker-body"><div className="adc-artifact-check-grid">{selectableArtifacts.map(item => <label key={item.id} className={`adc-artifact-check ${selectedArtifactIds.includes(item.id) ? 'selected' : ''}`}><Checkbox checked={selectedArtifactIds.includes(item.id)} onChange={e => setSelectedArtifactIds(prev => e.target.checked ? Array.from(new Set([...prev, item.id])) : prev.filter(id => id !== item.id))} /><div><strong>{item.name}</strong><p>{item.desc}</p><Tag>{item.format === 'word' ? 'Word · 宋体/1.5倍行距' : artifactFormatLabel(item.format)}</Tag></div></label>)}</div></div></div>
            <div className="adc-generation-stage"><div className="adc-stage-head"><strong>生成进度</strong><span>{runningCount ? `正在生成 ${runningCount} 个，最多 3 并发` : '点击开始生成后这里会实时显示每个产物状态和后台步骤'}</span></div>{runnableArtifacts.map(item => <div className={`adc-progress-card ${item.status}`} key={item.id} id={`adc-progress-card-${item.id}`}><div className="adc-progress-title"><strong>{item.name}</strong><span className={statusClass(item.status)}>{item.status === 'running' && <LoadingOutlined spin />} {statusLabel(item.status)}</span></div>{item.status === 'running' && item.progress > 0 && <Progress percent={item.progress || 0} status="active" />}<p>{item.error || item.step || (item.status === 'review' ? '已生成完成，可在成果与确认页预览、下载或确认。' : item.status === 'ppt_review' ? '提示词已生成，正在逐页生成图片…' : item.status === 'queued' ? '正在排队等待并发槽位。' : item.status === 'running' ? '正在引用会议纪要、共创资料和知识库生成正文。' : item.desc)}</p>{!(item.status === 'review' || item.status === 'confirmed' || item.status === 'ppt_review') && (item.steps?.length || item.status === 'running' || item.status === 'queued') ? <Timeline className="adc-step-flow" items={(item.steps || [item.step || GENERATION_STEPS[0]]).map(step => ({ children: step, color: item.status === 'failed' ? 'red' : item.status === 'confirmed' ? 'green' : 'blue' }))} /> : null}{item.id === 'timeline' && <div style={{marginTop:8}}><span style={{fontSize:12,color:'#64748b',marginRight:8}}>截止时间：</span><input type="date" value={timelineDeadline} onChange={e => setTimelineDeadline(e.target.value)} style={{padding:'4px 8px',borderRadius:8,border:'1px solid #d1d5db',fontSize:13}} /></div>}<div style={{marginTop:8}}><VoiceNoteInput value={artifactNotes[item.id] || ''} onChange={val => setArtifactNotes(prev => ({...prev, [item.id]: val}))} /></div><div className="adc-progress-actions"><Button className="adc-clean-default" size="small" disabled={!analysis || item.status === 'running'} onClick={() => handleRegenerateArtifact(item)}>{item.content || item.status === 'failed' ? '重新生成' : '单独生成'}</Button><Button className="adc-clean-default" size="small" disabled={!item.content || item.status === 'running'} icon={<EditOutlined />} onClick={() => handleAdjustArtifact(item)}>按意见调整</Button>{item.content && <Button size="small" onClick={() => setPreview(item)}>预览</Button>}</div></div>)}</div>
          </section> },
          { key: 'outputs', label: '成果与确认', children: <section className="adc-output-page"><div className="adc-output-hero"><div><span className="adc-output-kicker">成果与确认</span><h3>只展示真实生成结果</h3><p>这里不会再放演示假数据。产物生成完成后，才会出现在下方用于预览、下载和确认。</p></div><div className="adc-output-stats"><div><strong>{generatedArtifacts.length}</strong><span>真实产物</span></div><div><strong>{confirmedCount}</strong><span>已确认</span></div>{generatedArtifacts.length > 0 && <Button className="adc-clean-primary" type="primary" size="large" icon={<DownloadOutlined />} onClick={async () => { const items = generatedArtifacts.filter((a:any) => a.content); if(!items.length) return messageApi.warning('没有可下载的产物'); messageApi.loading({content:`正在打包下载 ${items.length} 个文件…`,key:'batch-dl',duration:0}); for(let i=0;i<items.length;i++){ const a=items[i]; try { const ext = a.format==='word'?'docx':a.format==='md'?'md':a.format==='ppt'?'pptx':'html'; await new Promise(r=>setTimeout(r,600)); const blob = new Blob([a.content||''],{type:ext==='docx'?'application/vnd.openxmlformats-officedocument.wordprocessingml.document':'text/plain'}); const url=URL.createObjectURL(blob); const el=document.createElement('a');el.href=url;el.download=`${a.name}.${ext}`;el.click();URL.revokeObjectURL(url); } catch(e:any){} } messageApi.success({content:`已触发 ${items.length} 个文件下载`,key:'batch-dl'}); }}>📦 打包下载全部</Button>}</div></div>{generatedArtifacts.length === 0 ? <div className="adc-output-empty"><FileTextOutlined /><h3>暂无可确认成果</h3><p>请先到“共创产物”选择产物并点击开始生成。生成完成后，这里只展示真实产物，不再展示占位假数据。</p><Button className="adc-clean-primary" type="primary" icon={<SendOutlined />} onClick={() => setActiveTab('templates')}>去生成产物</Button></div> : <div className="adc-output-grid">{generatedArtifacts.map(item => <article className={`adc-output-card ${item.status}`} key={item.id}><div className="adc-output-card-head"><div><Tag color={item.type === 'rd' ? 'purple' : 'blue'}>{item.level}</Tag><Tag>{artifactFormatLabel(item.format)}</Tag></div><span className={statusClass(item.status)}>{item.status === 'running' && <LoadingOutlined spin />} {statusLabel(item.status)}</span></div><h4>{item.name}</h4><div className="adc-output-meta"><span>生成时间：{formatDateTime(item.generatedAt)}</span><span>文件大小：{item.format === 'ppt' ? pptSizeLabel(item) : formatFileSize(item.fileSize || (item.content ? new Blob([item.content]).size : 0))}</span></div>{item.error && <p className="adc-output-error">{item.error}</p>}{(item.status === 'running' || item.status === 'queued') && <Progress percent={item.progress || 0} status={item.status === 'running' ? 'active' : undefined} />}<div className="adc-output-actions"><Button className="adc-clean-default" disabled={!item.content} onClick={() => setPreview(item)}>预览</Button>{item.format === 'image' ? <Button className="adc-clean-default" disabled={!imageArtifactUrl(item)} onClick={() => { const url = imageArtifactUrl(item); if (!url) return messageApi.error('未找到图片地址'); const a=document.createElement('a'); a.href=url; a.download=`${item.name}.png`; a.click(); }}>下载图片</Button> : item.format === 'ppt' ? <Button className="adc-clean-default" disabled={!item.content} onClick={async () => { try { const pid = JSON.parse(item.content||'{}').pptProjectId; if(!pid) return messageApi.error('未找到PPT项目'); messageApi.loading({content:'正在生成PPTX…',key:'pptx',duration:0}); await api.post(`/ppt-projects/${pid}/build-pptx`, { selectedSlideIds: [] }); const r2 = await api.post(`/ppt-projects/${pid}/download-ticket`, {}); if(r2.data?.url) { const a2 = document.createElement('a'); a2.href = r2.data.url; a2.download = item.name+'.pptx'; a2.click(); messageApi.success({content:'PPTX下载已开始',key:'pptx'}); } } catch(e2:any) { messageApi.error({content:e2?.response?.data?.message||'生成PPTX失败',key:'pptx'}); } }}>下载PPTX</Button> : <><Button className="adc-clean-default" disabled={!item.content} onClick={() => { downloadOfficeFile(item.content||'', item.name, 'docx', useAuthStore.getState().token||'', (m)=>messageApi.success(m), (e)=>messageApi.error(e), ()=>messageApi.loading({content:'正在生成文件…',key:'dl',duration:0}), (k:any)=>messageApi.destroy(k)); }}>Word</Button><Button className="adc-clean-default" disabled={!item.content} onClick={() => { downloadOfficeFile(item.content||'', item.name, 'pdf', useAuthStore.getState().token||'', (m)=>messageApi.success(m), (e)=>messageApi.error(e), ()=>messageApi.loading({content:'正在生成文件…',key:'dl',duration:0}), (k:any)=>messageApi.destroy(k)); }}>PDF</Button></>}<Button className="adc-clean-default" disabled={item.status === 'running'} onClick={() => handleRegenerateArtifact(item)}>重新生成</Button>{item.status !== 'confirmed' && <Button className="adc-clean-primary" type="primary" disabled={item.status !== 'review'} onClick={() => handleConfirmArtifact(item.id)}>确认此版本</Button>}</div></article>)}</div>}</section> },

        ]} />
      </main>

      <Drawer title="历史共创" open={historyOpen} onClose={() => setHistoryOpen(false)} width={560} className="adc-manager-drawer">
        <div className="adc-manager-head"><HistoryOutlined /><div><h3>历史共创任务</h3><p>选择一个历史任务继续查看录音、会议纪要和产物。</p></div></div>
        <div className="adc-history-list">
          {demandTasks.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无历史共创任务" /> : demandTasks.map(task => <button type="button" className={`adc-history-item ${task.id === currentTaskId ? 'active' : ''}`} key={task.id} onClick={() => { loadTask(task.id); setHistoryOpen(false); }}><div><strong>{task.name}</strong><span>{new Date(task.updatedAt || task.createdAt).toLocaleString()}</span></div><Tag color={task.status === 'analyzed' ? 'green' : task.status === 'recorded' ? 'blue' : 'default'}>{task.status === 'draft' ? '草稿' : task.status === 'recorded' ? '已录音' : task.status === 'analyzed' ? '已分析' : task.status}</Tag></button>)}
        </div>
        <Button className="adc-clean-primary adc-manager-full-btn" type="primary" icon={<PlusOutlined />} onClick={() => { handleNewTask(); setHistoryOpen(false); setActiveTab('meeting'); }}>新建共创任务</Button>
      </Drawer>
      <Drawer title="模板管理" open={templateOpen} onClose={() => setTemplateOpen(false)} width={680} className="adc-manager-drawer">
        <div className="adc-manager-head"><SettingOutlined /><div><h3>共创产物模板</h3><p>当前内置模板可直接选择使用，后续可在这里扩展企业模板。</p></div></div>
        <div className="adc-template-manage-list">
          {TEMPLATES.map(item => <button type="button" key={item.id} className={`adc-template-manage-card ${item.id === templateId ? 'active' : ''}`} onClick={() => { setTemplateId(item.id); setTemplateOpen(false); setActiveTab('templates'); }}><div className="adc-template-manage-top"><BookOutlined /><Tag color={item.id === templateId ? 'cyan' : 'default'}>{item.id === templateId ? '当前使用' : '可选择'}</Tag></div><h4>{item.name}</h4><p>{item.desc}</p><div>{item.chips.map(chip => <Tag key={chip}>{chip}</Tag>)}</div></button>)}
        </div>
        <Alert type="info" showIcon message="模板管理已接入当前页面选择逻辑" description="点击模板即可切换到对应共创产物配置；自定义模板保存功能后续可接后端持久化。" />
      </Drawer>
      <Modal title="上传到 AI需求共创资料夹" open={uploadOpen} onCancel={() => setUploadOpen(false)} onOk={handleFakeUpload} okText="放入资料夹" cancelText="取消" width={620}><Upload.Dragger multiple fileList={selectedFiles} beforeUpload={file => { setSelectedFiles(prev => [...prev, file]); return false; }} onRemove={file => setSelectedFiles(prev => prev.filter(item => item.uid !== file.uid))} accept=".pdf,.doc,.docx,.txt,.md,.png,.jpg,.jpeg,.mp3,.wav,.m4a"><p className="ant-upload-drag-icon"><CloudUploadOutlined /></p><p className="ant-upload-text">拖拽文件到这里，或点击选择</p><p className="ant-upload-hint">支持会议资料、需求文档、流程图、截图、录音文件。</p></Upload.Dragger></Modal>

      <Modal title="腾讯会议授权登录" open={tmeetAuthModalOpen} onCancel={closeTmeetAuthModal} footer={null} width={440} destroyOnClose>
        <div className="adc-tmeet-auth-modal">
          {tmeetAuthPhase === 'starting' && (
            <div className="adc-tmeet-auth-loading"><LoadingOutlined spin /> 正在发起授权，请稍候…</div>
          )}
          {tmeetAuthPhase === 'waiting' && (
            <>
              <ol className="adc-tmeet-auth-steps">
                <li>用手机扫描下方二维码，或点击链接在浏览器打开授权页面</li>
                <li>在腾讯会议页面完成登录并同意授权</li>
                <li>回到本页，授权结果会自动生效</li>
              </ol>
              <div className="adc-tmeet-auth-qr"><QRCode value={tmeetAuthUrl} size={184} /></div>
              <div className="adc-tmeet-auth-link">
                <Typography.Link href={tmeetAuthUrl} target="_blank" rel="noreferrer">在新窗口打开授权页面</Typography.Link>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => { navigator.clipboard?.writeText(tmeetAuthUrl).then(() => messageApi.success('授权链接已复制')).catch(() => messageApi.warning('复制失败，请手动复制')); }}
                >
                  复制链接
                </Button>
              </div>
              <div className="adc-tmeet-auth-waiting"><LoadingOutlined spin /> 等待授权完成…（链接 10 分钟内有效）</div>
            </>
          )}
          {tmeetAuthPhase === 'success' && (
            <div className="adc-tmeet-auth-result">
              <CheckCircleOutlined className="adc-tmeet-auth-ok" />
              <p>授权成功，腾讯会议功能已可用</p>
              <Button type="primary" onClick={closeTmeetAuthModal}>完成</Button>
            </div>
          )}
          {tmeetAuthPhase === 'failed' && (
            <div className="adc-tmeet-auth-result">
              <Alert type="error" showIcon message="授权未完成" description={tmeetAuthError || '请重试'} />
              <Button type="primary" onClick={startTmeetAuthLogin}>重新发起授权</Button>
            </div>
          )}
        </div>
      </Modal>
      {!!preview && createPortal(<div className="adc-preview-overlay" onClick={() => setPreview(null)}><div className="adc-preview-card" onClick={e => e.stopPropagation()}><div className="adc-preview-card-head"><strong>{preview?.name || '产物预览'}</strong><button className="adc-preview-close" onClick={() => setPreview(null)}>✕</button></div><div className="adc-demo-doc-reader"><div className="adc-demo-doc-head"><Tag>{preview.level}</Tag><Tag color={preview.type === 'rd' ? 'purple' : preview.type === 'pre' ? 'blue' : 'green'}>{preview.type === 'rd' ? '研发产物' : preview.type === 'pre' ? '产前产物' : '基础产物'}</Tag><Tag>{preview.format === 'word' ? 'Word · 宋体 · 1.5倍行距' : artifactFormatLabel(preview.format)}</Tag><span className={statusClass(preview.status)}>{statusLabel(preview.status)}</span></div>{(() => { const pid = (() => { try { return JSON.parse(preview.content||"{}").pptProjectId; } catch { return null; } })(); return preview.format === 'image' ? (imageArtifactUrl(preview) ? <div className="adc-demo-doc-section"><img src={imageArtifactUrl(preview)} alt={preview.name} style={{ width: '100%', borderRadius: 16, border: '1px solid rgba(148,163,184,.28)' }} /></div> : <div className="adc-demo-doc-section"><h4>图片生成结果缺少URL</h4><p>请重新生成该工程图产物。</p></div>) : pid ? <PptSlideViewer pptProjectId={pid} artifactName={preview.name} onConfirm={() => { void persistArtifactStatus(preview.id, 'review', 'PPT图片已生成，等待确认', 'PPT图片已生成，刷新页面后仍会保留'); setMascotAnnouncement({ id: `ppt-done-${preview.id}-${Date.now()}`, text: `${preview.name}的PPT图片全部生成好啦～` }); setPreview(null); }} /> : preview.content ? <iframe className="adc-generated-html-iframe" srcDoc={preview.content} sandbox="allow-same-origin" title="预览内容" /> : <div className="adc-demo-doc-section"><h4>暂未生成正文</h4><p>请先在"共创产物"页面点击开始生成；生成完成后这里会展示可预览、可复制、可下载的正文。</p></div>; })()}<div className="adc-demo-doc-actions"><Button icon={<CopyOutlined />} disabled={!preview.content} onClick={() => { const text = preview.content?.replace(/<[^>]+>/g, '').replace(/&[^;]+;/g, ' ').replace(/\s+/g, ' ').trim() || preview.preview; navigator.clipboard?.writeText(text); messageApi.success('已复制正文'); }}>复制正文</Button>{preview.format === 'image' ? <Button disabled={!imageArtifactUrl(preview)} onClick={() => { const url = imageArtifactUrl(preview); if (!url) return messageApi.error('未找到图片地址'); const a=document.createElement('a'); a.href=url; a.download=`${preview.name}.png`; a.click(); }}>下载图片</Button> : <><Button disabled={!preview.content} onClick={() => { downloadOfficeFile(preview.content||'', preview.name, 'docx', useAuthStore.getState().token||'', (m)=>messageApi.success(m), (e)=>messageApi.error(e), ()=>messageApi.loading({content:'正在生成文件…',key:'dl',duration:0}), (k:any)=>messageApi.destroy(k)); }}>Word</Button><Button disabled={!preview.content} onClick={() => { downloadOfficeFile(preview.content||'', preview.name, 'pdf', useAuthStore.getState().token||'', (m)=>messageApi.success(m), (e)=>messageApi.error(e), ()=>messageApi.loading({content:'正在生成文件…',key:'dl',duration:0}), (k:any)=>messageApi.destroy(k)); }}>PDF</Button></>}<Button icon={<ReloadOutlined />} disabled={preview.id === 'minutes' || preview.status === 'running'} onClick={() => handleRegenerateArtifact(preview)}>重新生成</Button>{preview.status !== 'confirmed' && <Button type="primary" icon={<FileDoneOutlined />} disabled={preview.status !== 'review'} onClick={() => handleConfirmArtifact(preview.id)}>确认此版本</Button>}</div><div className="adc-demo-doc-meta"><div><SafetyCertificateOutlined /><span>引用资料：{preview.references?.materials?.length ? preview.references.materials.join('、') : '未引用'}{preview.references?.knowledgeBase ? ' + 知识库' : ''}</span></div><div><FileTextOutlined /><span>格式：{artifactFormatLabel(preview.format)}{preview.fileSize ? ` · ${(preview.fileSize / 1024).toFixed(1)} KB` : ''}{preview.generatedAt ? ` · ${new Date(preview.generatedAt).toLocaleString()}` : ''}</span></div></div></div></div></div>, document.body)}
    </div>
  );
}
